import { PreparationIdentity } from './components/PreparationIdentity';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  fetchBatch,
  fetchBatchAuditTrail,
  fetchBatchMeasurements,
  fetchBatchSamples,
  importBatchSamples,
  updateBatch,
  excludeMeasurement,
  includeMeasurement,
  autoGroupBatch,
  confirmGrouping,
  fetchMeasurementTraces,
  selectMeasurementTrace,
  fetchMeasurementPreview,
  previewDerivedRun,
  saveDerivedRun,
  confirmSampleComposition,
  fetchBatchRunList,
  fetchBatchRunSavedDetail,
  fetchBatchSizeClusters,
  fetchSizeSetupDefaults,
} from '../../services/batchService';
import {
  buildDerivedSeedFromSavedRunDetail,
  canExploreDerivedSeed,
  buildDerivedPreviewPayload,
  buildDerivedSavePayload,
  DERIVED_PREVIEW_STATUS,
  getDerivedPreviewSignature,
  getPreviewStatusAfterDerivedDraftChange,
  getSuggestedRerunReasonCodeForSeed,
  hasDerivedDraftChanges,
  hasFreshDerivedPreview,
  seedDerivedDraftFromSeed,
  shouldApplyDerivedPreviewResponse,
} from '../../utils/derivedRunDraft';
import {
  buildSetupPayload,
  emptyExperimentalFields,
  extractApiError,
  findMissingExperimentalFields,
  formatMissingMessage,
  mapSetupDetailToFields,
} from '../../utils/experimentalSetups';
import {
  clearRetainedExecutionRequestSubmission,
  retainExecutionRequestSubmission,
} from '../../utils/executionRequestIdentity';
import { usePipelinePreparation, PREPARATION_PIPELINES, isCurrentMeasurement } from './hooks/usePipelinePreparation';
import { MeasurementHistory } from './components/MeasurementHistory';
import { useExecutionRequestPolling } from './hooks/useExecutionRequestPolling';
import { InitialRunPreparation } from './components/InitialRunPreparation';
import { DerivedRunExplorer } from './DerivedRunExplorer';
import { formatRunTimestamp } from '../../utils/runFormatting';
import { formatAuditEvent } from '../../utils/auditFormatting';
import { CompositionForm } from '../CompositionForm';
import { RunInputFiles } from '../RunInputFiles';
import SizePlot from '../SizePlot';
import { RunAnalysisView } from './RunAnalysisView';
import { SizeSetupForm } from './SizeSetupForm';

const DERIVED_PREVIEW_DEBOUNCE_MS = 500;
const ACTIVE_DERIVED_EXECUTION_STATUSES = new Set([
  'pending',
  'planned',
  'processing',
  'running',
]);
const SIZE_CLUSTER_COLORS = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#ca8a04',
  '#0891b2',
];

function formatClusterNumber(value, digits = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return parsed.toFixed(digits);
}

function sizeClusterStatusLabel(status) {
  const labels = {
    clustered: 'Clustered',
    no_clusters: 'No clusters',
    insufficient_data: 'Insufficient data',
    no_completed_runs: 'No completed size runs',
    no_components: 'No components',
    invalid_features: 'Invalid component values',
  };
  return labels[status] || status || 'Unknown';
}

function finiteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function findNearestTracePoint(series, t0) {
  const target = finiteNumber(t0);
  if (target === null || !Array.isArray(series?.time) || !Array.isArray(series?.signal)) {
    return null;
  }
  let best = null;
  series.time.forEach((timeValue, idx) => {
    const time = finiteNumber(timeValue);
    const signal = finiteNumber(series.signal[idx]);
    if (time === null || signal === null) return;
    const distance = Math.abs(time - target);
    if (!best || distance < best.distance) {
      best = { time, signal, distance };
    }
  });
  return best;
}

function buildSizeClusterOverlay(sample) {
  const series = Array.isArray(sample?.taylorgram_series) ? sample.taylorgram_series : [];
  const rowsByTime = new Map();
  const lines = [];

  series.forEach((item, idx) => {
    const key = `trace_${idx}`;
    const color = SIZE_CLUSTER_COLORS[idx % SIZE_CLUSTER_COLORS.length];
    lines.push({
      key,
      color,
      name: `Measurement ${item.input_import_id || idx + 1}`,
    });
    (item.time || []).forEach((timeValue, pointIdx) => {
      const time = finiteNumber(timeValue);
      const signal = finiteNumber(item.signal?.[pointIdx]);
      if (time === null || signal === null) return;
      const mapKey = String(time);
      const row = rowsByTime.get(mapKey) || { time };
      row[key] = signal;
      rowsByTime.set(mapKey, row);
    });
  });

  const data = Array.from(rowsByTime.values()).sort((left, right) => left.time - right.time);
  const seriesByRun = new Map();
  const seriesByInput = new Map();
  series.forEach((item) => {
    if (item.workflow_run_id !== undefined && item.workflow_run_id !== null) {
      seriesByRun.set(String(item.workflow_run_id), item);
    }
    if (item.input_import_id !== undefined && item.input_import_id !== null) {
      seriesByInput.set(String(item.input_import_id), item);
    }
  });

  const markers = [];
  (sample?.clusters || []).forEach((cluster, clusterIdx) => {
    const color = SIZE_CLUSTER_COLORS[clusterIdx % SIZE_CLUSTER_COLORS.length];
    (cluster.components || []).forEach((component, componentIdx) => {
      const sourceSeries =
        seriesByRun.get(String(component.workflow_run_id)) ||
        seriesByInput.get(String(component.input_import_id));
      const point = findNearestTracePoint(sourceSeries, component.t0);
      if (!point) return;
      markers.push({
        ...point,
        color,
        label: `C${cluster.cluster_id}`,
        key: `cluster-${cluster.cluster_id}-${component.workflow_run_id}-${component.input_import_id}-${componentIdx}`,
      });
    });
  });
  (sample?.unclustered_components || []).forEach((component, componentIdx) => {
    const sourceSeries =
      seriesByRun.get(String(component.workflow_run_id)) ||
      seriesByInput.get(String(component.input_import_id));
    const point = findNearestTracePoint(sourceSeries, component.t0);
    if (!point) return;
    markers.push({
      ...point,
      color: '#64748b',
      label: 'U',
      key: `unclustered-${component.workflow_run_id}-${component.input_import_id}-${componentIdx}`,
    });
  });

  return { data, lines, markers };
}

function sizeClusterSampleKey(sample) {
  return String(sample?.batch_sample_id || sample?.sample_id || '');
}

function dedupeIntegerIds(ids = []) {
  const seen = new Set();
  const out = [];
  ids.forEach((id) => {
    const parsed = Number(id);
    if (!Number.isInteger(parsed) || seen.has(parsed)) return;
    seen.add(parsed);
    out.push(parsed);
  });
  return out;
}

function sameMeasurementIds(left = [], right = []) {
  if (left.length !== right.length) return false;
  const leftSorted = [...left].sort((a, b) => a - b);
  const rightSorted = [...right].sort((a, b) => a - b);
  return leftSorted.every((id, idx) => id === rightSorted[idx]);
}

function previewConfidenceRank(value) {
  if (value === 'low') return 2;
  if (value === 'medium') return 1;
  return 0;
}

function buildClusterPreview(cluster, measurementPreviewById = {}) {
  const measurements = cluster.measurement_ids
    .map((measurementId) => measurementPreviewById[measurementId])
    .filter(Boolean);
  const detectedConditions = Array.from(
    new Set(measurements.map((item) => item?.parsed?.condition).filter(Boolean))
  );
  const detectedTimepoints = Array.from(
    new Set(measurements.map((item) => item?.parsed?.timepoint).filter(Boolean))
  );
  const detectedTemperatures = Array.from(
    new Set(measurements.map((item) => item?.parsed?.temperature).filter(Boolean))
  );
  const previewWarnings = [];
  const seenWarnings = new Set();
  let worstConfidenceRank = 0;

  measurements.forEach((item) => {
    const parsed = item?.parsed || {};
    worstConfidenceRank = Math.max(worstConfidenceRank, previewConfidenceRank(parsed.confidence));
    (parsed.warnings || []).forEach((warning) => {
      if (seenWarnings.has(warning)) return;
      seenWarnings.add(warning);
      previewWarnings.push(warning);
    });
  });

  if (detectedConditions.length > 1) {
    const warning = 'Measurements in this group include multiple detected conditions.';
    if (!seenWarnings.has(warning)) {
      seenWarnings.add(warning);
      previewWarnings.push(warning);
    }
    worstConfidenceRank = Math.max(worstConfidenceRank, 1);
  }
  if (detectedTimepoints.length > 1) {
    const warning = 'Measurements in this group include multiple detected timepoints.';
    if (!seenWarnings.has(warning)) {
      seenWarnings.add(warning);
      previewWarnings.push(warning);
    }
    worstConfidenceRank = Math.max(worstConfidenceRank, 1);
  }
  if (detectedTemperatures.length > 1) {
    const warning = 'Measurements in this group include multiple detected temperatures.';
    if (!seenWarnings.has(warning)) {
      seenWarnings.add(warning);
      previewWarnings.push(warning);
    }
    worstConfidenceRank = Math.max(worstConfidenceRank, 1);
  }

  const sourcePreview = cluster.serverPreview;
  const previewMatchesCurrentDraft =
    sourcePreview &&
    cluster.sample_name.trim() === (sourcePreview.proposed_sample_name || '').trim() &&
    sameMeasurementIds(cluster.measurement_ids, sourcePreview.measurement_ids || []);

  if (previewMatchesCurrentDraft) {
    worstConfidenceRank = Math.max(
      worstConfidenceRank,
      previewConfidenceRank(sourcePreview.confidence)
    );
    (sourcePreview.warnings || []).forEach((warning) => {
      if (seenWarnings.has(warning)) return;
      seenWarnings.add(warning);
      previewWarnings.push(warning);
    });
  }

  const confidence =
    worstConfidenceRank >= 2 ? 'low' : worstConfidenceRank === 1 ? 'medium' : 'high';

  return {
    proposed_sample_name: cluster.sample_name,
    measurements,
    detected_conditions: detectedConditions,
    detected_timepoints: detectedTimepoints,
    detected_temperatures: detectedTemperatures,
    existing_sample_match: previewMatchesCurrentDraft
      ? sourcePreview.existing_sample_match || null
      : null,
    confirmation_effect: previewMatchesCurrentDraft
      ? sourcePreview.confirmation_effect || null
      : null,
    confidence,
    warnings: previewWarnings,
  };
}

function normalizeGroupingDraft(result) {
  const measurementPreviewById = { ...(result?.measurementPreviewById || {}) };
  const groups = Array.isArray(result?.groups) ? result.groups : [];
  groups.forEach((group) => {
    (group?.measurements || []).forEach((measurement) => {
      const parsedId = Number(measurement?.id);
      if (!Number.isInteger(parsedId)) return;
      measurementPreviewById[parsedId] = measurement;
    });
  });
  const assigned = new Set();
  const clusters = (result?.clusters || []).map((cluster) => {
    const cleanedIds = dedupeIntegerIds(cluster?.measurement_ids || []).filter((id) => {
      if (assigned.has(id)) return false;
      assigned.add(id);
      return true;
    });
    const matchingGroup =
      groups.find(
        (group) =>
          sameMeasurementIds(dedupeIntegerIds(group?.measurement_ids || []), cleanedIds) &&
          ((group?.proposed_sample_name || '').trim() === (cluster?.sample_name || '').trim())
      ) || null;
    return {
      sample_name: (cluster?.sample_name || '').trim(),
      measurement_ids: cleanedIds,
      serverPreview:
        cluster?.serverPreview || (matchingGroup
          ? {
              proposed_sample_name: (matchingGroup?.proposed_sample_name || '').trim(),
              measurement_ids: dedupeIntegerIds(matchingGroup?.measurement_ids || []),
              existing_sample_match: matchingGroup?.existing_sample_match || null,
              confirmation_effect: matchingGroup?.confirmation_effect || null,
              confidence: matchingGroup?.confidence || 'high',
              warnings: matchingGroup?.warnings || [],
            }
          : null),
    };
  });
  const unclusterable = dedupeIntegerIds(result?.unclusterable || []).filter(
    (id) => !assigned.has(id)
  );
  return { clusters, unclusterable, measurementPreviewById,
    pipeline: result?.pipeline, cycle_number: result?.cycle_number,
    preparation_revision: result?.preparation_revision };
}

function generateClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return (
      `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
      `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
    );
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const rnd = Math.floor(Math.random() * 16);
    const val = ch === 'x' ? rnd : (rnd & 0x3) | 0x8;
    return val.toString(16);
  });
}

function getDerivedExecutionPlanItem(executionRequest) {
  return Array.isArray(executionRequest?.plan_items)
    ? executionRequest.plan_items[0] || null
    : null;
}

function getDerivedExecutionStatus(executionRequest) {
  const planItem = getDerivedExecutionPlanItem(executionRequest);
  return String(
    planItem?.status ||
      planItem?.latest_attempt?.status ||
      executionRequest?.status ||
      ''
  ).toLowerCase();
}

function getDerivedWorkflowRunId(executionRequest) {
  const planItem = getDerivedExecutionPlanItem(executionRequest);
  return planItem?.workflow_run_id || planItem?.latest_attempt?.workflow_run_id || null;
}

function encodeMeasurementTraceIdentity(trace) {
  return JSON.stringify({
    trace_id: trace?.trace_id ?? null,
    wavelength_nm: trace?.wavelength_nm ?? null,
  });
}

function measurementTraceIdentityMatches(trace, identity) {
  return (
    String(trace?.trace_id || '') === String(identity?.trace_id || '') &&
    String(trace?.wavelength_nm ?? '') === String(identity?.wavelength_nm ?? '')
  );
}

function persistedMeasurementTraceIdentity(source) {
  const traceId = String(source?.selected_trace_id || '').trim() || null;
  const wavelengthNm = source?.selected_wavelength_nm ?? null;
  return traceId || wavelengthNm != null
    ? { trace_id: traceId, wavelength_nm: wavelengthNm }
    : null;
}

function formatTraceScore(score) {
  if (score == null) return 'Not scored';
  const numericScore = Number(score);
  const displayedScore = Number.isFinite(numericScore)
    ? numericScore.toLocaleString(undefined, { maximumFractionDigits: 4 })
    : String(score);
  return `Score: ${displayedScore}`;
}

function SizeClusterOverlay({ sample }) {
  const series = Array.isArray(sample?.taylorgram_series) ? sample.taylorgram_series : [];
  const { data, lines, markers } = useMemo(
    () => buildSizeClusterOverlay(sample),
    [sample]
  );

  if (!series.length || !data.length || !lines.length) {
    return null;
  }

  return (
    <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Overlay traces</h4>
          <p className="text-xs text-slate-500">
            {series.length} source trace{series.length === 1 ? '' : 's'} with cluster markers.
          </p>
        </div>
        {sample?.plot_series_metadata?.missing_source_run_ids?.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
            {sample.plot_series_metadata.missing_source_run_ids.length} missing plot
            {sample.plot_series_metadata.missing_source_run_ids.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value) => (typeof value === 'number' ? value.toFixed(3) : value)} />
            <Legend />
            {lines.map((line) => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                name={line.name}
                stroke={line.color}
                dot={false}
                strokeWidth={1.8}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            {markers.map((marker) => (
              <ReferenceDot
                key={marker.key}
                x={marker.time}
                y={marker.signal}
                r={4}
                fill={marker.color}
                stroke="#ffffff"
                strokeWidth={1}
                label={{ value: marker.label, position: 'top', fontSize: 10 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function BatchDetailPanel({
  batchId,
  initialSampleId,
  initialPipeline,
}) {
  const [searchParams] = useSearchParams();
  const focusRunId = searchParams.get('focusRun');
  const normalizedInitialPipeline = (initialPipeline || '').toLowerCase();
  const [batch, setBatch] = useState(null);
  const [samples, setSamples] = useState([]);
  const [runs, setRuns] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [sizeClusters, setSizeClusters] = useState(null);
  const [sizeClustersLoading, setSizeClustersLoading] = useState(false);
  const [sizeClustersError, setSizeClustersError] = useState(null);
  const [expandedSizeClusterOverlays, setExpandedSizeClusterOverlays] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [sampleFilters, setSampleFilters] = useState({
    search: '',
  });

  const [metadataFields, setMetadataFields] = useState({
    code: '',
    protocol: '',
  });
  const [sizeUploadInstrument, setSizeUploadInstrument] = useState('');
  const [encapsulationUploadInstrument, setEncapsulationUploadInstrument] =
    useState('');
  const [sizeSetupFields, setSizeSetupFields] = useState({
    ...emptyExperimentalFields(),
  });
  const [sizeSetupLoading, setSizeSetupLoading] = useState(true);
  const [sizeSetupError, setSizeSetupError] = useState(null);
  const [activeTab, setActiveTab] = useState('data');
  const initializedNavigation = useRef(null);
  const [selectedRunRow, setSelectedRunRow] = useState(null);
  const [selectedRunDetail, setSelectedRunDetail] = useState(null);
  const [selectedRunDetailLoading, setSelectedRunDetailLoading] = useState(false);
  const [selectedRunDetailError, setSelectedRunDetailError] = useState(null);
  const [derivedDraft, setDerivedDraft] = useState(null);
  const [runSubmissionError, setRunSubmissionError] = useState(null);
  const [submittedDerivedExecutionRequest, setSubmittedDerivedExecutionRequest] =
    useState(null);
  const [derivedPublicationFailure, setDerivedPublicationFailure] = useState(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [showMetaEditor, setShowMetaEditor] = useState(false);
  const [sizeUploadState, setSizeUploadState] = useState({
    uploading: false,
    lastCount: 0,
    lastUploadedAt: null,
  });
  const [sizeUploadError, setSizeUploadError] = useState(null);
  const [eeUploadState, setEeUploadState] = useState({
    uploading: false,
    lastCount: 0,
    lastUploadedAt: null,
  });
  const [eeErrors, setEeErrors] = useState(null);
  const [pendingUploadPreview, setPendingUploadPreview] = useState(null);
  const [measurementSnapshot, setMeasurementSnapshot] = useState({ batchId: null, items: [] });
  const allMeasurements = useMemo(() => measurementSnapshot.batchId === batchId ? measurementSnapshot.items : [], [measurementSnapshot, batchId]);
  const setMeasurements = useCallback((value) => setMeasurementSnapshot((previous) => ({
    batchId,
    items: typeof value === 'function' ? value(previous.batchId === batchId ? previous.items : []) : value,
  })), [batchId]);
  const [selectedPipeline, setSelectedPipeline] = useState(
    PREPARATION_PIPELINES.includes(normalizedInitialPipeline) ? normalizedInitialPipeline : 'size'
  );
  const { state: preparationState, currentMeasurements: measurements, historyMeasurements,
    groupingDraft, setGroupingDraft, expandedGroupingClusters, setExpandedGroupingClusters,
    importErrors, setImportErrors, preparationNotice, setPreparationNotice, preparationReloadRequired, requirePreparationReload,
  } = usePipelinePreparation({ batchId, batch, measurements: allMeasurements, pipeline: selectedPipeline });
  const [groupingAutoLoading, setGroupingAutoLoading] = useState(false);
  const [groupingConfirming, setGroupingConfirming] = useState(false);
  const [recalculateOpen, setRecalculateOpen] = useState(false);
  const [previewMeasurement, setPreviewMeasurement] = useState(null);
  const [measurementPreview, setMeasurementPreview] = useState(null);
  const [measurementTraces, setMeasurementTraces] = useState(null);
  const [measurementTraceDraft, setMeasurementTraceDraft] = useState(null);
  const [tracePreparationScope, setTracePreparationScope] = useState(null);
  const [measurementMutating, setMeasurementMutating] = useState(false);
  const [savingMeasurementTrace, setSavingMeasurementTrace] = useState(false);
  const [loadingMeasurementPreview, setLoadingMeasurementPreview] = useState(false);
  const [loadingMeasurementTraces, setLoadingMeasurementTraces] = useState(false);
  const [measurementPreviewError, setMeasurementPreviewError] = useState(null);
  const measurementPreviewRequestIdRef = useRef(0);
  const measurementPreviewSessionRef = useRef(0);
  const [measurementTraceDraftTouched, setMeasurementTraceDraftTouched] = useState(false);
  useEffect(() => () => {
    measurementPreviewSessionRef.current += 1;
    measurementPreviewRequestIdRef.current += 1;
  }, []);
  const runsRef = useRef([]);
  const selectedRunRowRef = useRef(null);
  const selectedRunDetailRef = useRef(null);
  const derivedDraftRef = useRef(null);
  const handledDerivedExecutionRef = useRef(null);
  const latestDerivedPreviewRequestIdRef = useRef(0);
  const nextDerivedPreviewRequestIdRef = useRef(0);
  runsRef.current = runs;
  selectedRunRowRef.current = selectedRunRow;
  selectedRunDetailRef.current = selectedRunDetail;
  derivedDraftRef.current = derivedDraft;
  const derivedExecutionRequestId =
    submittedDerivedExecutionRequest?.execution_request_id || null;
  const {
    executionRequest: polledDerivedExecutionRequest,
    pollingError: derivedExecutionPollingError,
  } = useExecutionRequestPolling(derivedExecutionRequestId);
  const polledDerivedRequestMatches =
    polledDerivedExecutionRequest?.execution_request_id != null &&
    String(polledDerivedExecutionRequest.execution_request_id) ===
      String(derivedExecutionRequestId);
  const activeDerivedExecutionRequest = polledDerivedRequestMatches
    ? polledDerivedExecutionRequest
    : submittedDerivedExecutionRequest;
  const derivedExecutionStatus = getDerivedExecutionStatus(
    activeDerivedExecutionRequest
  );
  const derivedExecutionRequestStatus = String(
    activeDerivedExecutionRequest?.status || ''
  ).toLowerCase();
  const completedDerivedWorkflowRunId = getDerivedWorkflowRunId(
    activeDerivedExecutionRequest
  );
  const derivedExecutionIsActive =
    Boolean(derivedExecutionRequestId) &&
    ACTIVE_DERIVED_EXECUTION_STATUSES.has(derivedExecutionStatus);
  const selectedRunSeed = useMemo(
    () => buildDerivedSeedFromSavedRunDetail(selectedRunDetail),
    [selectedRunDetail]
  );
  const appliedFocusRunIdRef = useRef(null);
  const suppressedFocusRunIdRef = useRef(null);

  useEffect(() => {
    console.log('[BatchDetailPanel lifecycle] mount', {
      batchId,
      initialSampleId,
      initialPipeline,
    });
    return () => {
      console.log('[BatchDetailPanel lifecycle] unmount', {
        batchId,
        initialSampleId,
        initialPipeline,
      });
    };
  }, []);

  useEffect(() => {
    console.log('[BatchDetailPanel props] received', {
      batchId,
      initialSampleId,
      initialPipeline,
    });
  }, [batchId, initialSampleId, initialPipeline]);

  const logRunSelectionDebug = useCallback(
    (eventName, extra = {}) => {
      const currentRuns = Array.isArray(runsRef.current) ? runsRef.current : [];
      console.log('[BatchDetailPanel selection]', {
        eventName,
        focusRunId,
        appliedFocusRunId: appliedFocusRunIdRef.current,
        suppressedFocusRunId: suppressedFocusRunIdRef.current,
        selectedRunRowId: selectedRunRowRef.current?.id ?? null,
        derivedDraftSourceRunId: derivedDraftRef.current?.sourceRunId ?? null,
        runIds: currentRuns.slice(0, 5).map((run) => run?.id),
        runCount: currentRuns.length,
        ...extra,
      });
    },
    [focusRunId]
  );

  const loadBatch = useCallback(async () => {
    if (!batchId) {
      setBatch(null);
      return null;
    }
    const data = await fetchBatch(batchId);
    setBatch(data);
    setMetadataFields({
      code: data.code || '',
      protocol: data.protocol || '',
    });
    return data;
  }, [batchId]);

  const loadSamples = useCallback(async () => {
    if (!batchId) {
      setSamples([]);
      return;
    }
    const data = await fetchBatchSamples(batchId);
    setSamples(data);

  }, [batchId]);

  const loadRuns = useCallback(async () => {
    if (!batchId) {
      setRuns([]);
      return [];
    }
    const data = await fetchBatchRunList(batchId);
    setRuns(data);
    return data;
  }, [batchId]);

  const loadAudit = useCallback(async () => {
    if (!batchId) {
      setAuditEvents([]);
      return;
    }
    const events = await fetchBatchAuditTrail(batchId);
    setAuditEvents(events);
  }, [batchId]);

  const loadSizeClusters = useCallback(async ({ includeSeries = false } = {}) => {
    if (!batchId) {
      setSizeClusters(null);
      setSizeClustersError(null);
      return null;
    }
    setSizeClustersLoading(true);
    setSizeClustersError(null);
    try {
      const data = await fetchBatchSizeClusters(batchId, { includeSeries });
      setSizeClusters(data);
      return data;
    } catch (err) {
      const message = extractApiError(err, 'Failed to load size clusters.');
      setSizeClusters(null);
      setSizeClustersError(message);
      return null;
    } finally {
      setSizeClustersLoading(false);
    }
  }, [batchId]);

  const loadMeasurements = useCallback(async () => {
    if (!batchId) {
      setMeasurements([]);
      return;
    }
    const data = await fetchBatchMeasurements(batchId);
    setMeasurements(data);
  }, [batchId, setMeasurements]);

  const refreshPreparation = useCallback(async () => {
    const [fresh] = await Promise.all([loadBatch(), loadMeasurements(), loadSamples(), loadAudit()]);
    return fresh;
  }, [loadBatch, loadMeasurements, loadSamples, loadAudit]);

  const refreshAfterPreparationMutation = useCallback(async () => {
    try { await refreshPreparation(); }
    catch (_) {
      setGroupingDraft(null);
      requirePreparationReload();
      setPreparationNotice('Change saved, but refresh failed. Reload the workspace before making further preparation changes. Do not repeat the submitted operation.');
    }
  }, [refreshPreparation, setGroupingDraft, requirePreparationReload, setPreparationNotice]);

  const handlePreparationConflict = useCallback(async (error) => {
    if (error?.response?.status !== 409) return false;
    setGroupingDraft(null);
    setExpandedGroupingClusters({});
    setPreparationNotice('Preparation changed. Review the refreshed Measurements and generate a new grouping proposal before confirming. No mutation was retried.');
    try { await refreshPreparation(); }
    catch (_) { requirePreparationReload(); setPreparationNotice('Preparation changed, but refresh failed. Reload the workspace before making further changes.'); }
    return true;
  }, [refreshPreparation, setGroupingDraft, setExpandedGroupingClusters, setPreparationNotice, requirePreparationReload]);

  const refreshSizeClusters = useCallback(async () => {
    setExpandedSizeClusterOverlays({});
    return loadSizeClusters({ includeSeries: false });
  }, [loadSizeClusters]);

  const refreshInitialExecution = useCallback(async () => {
    setExpandedSizeClusterOverlays({});
    await Promise.all([loadRuns(), loadAudit(), loadSizeClusters()]);
  }, [loadRuns, loadAudit, loadSizeClusters]);

  const toggleSizeClusterOverlay = useCallback(
    async (sample) => {
      const key = sizeClusterSampleKey(sample);
      if (!key) return;
      if (expandedSizeClusterOverlays[key]) {
        setExpandedSizeClusterOverlays((current) => ({
          ...current,
          [key]: false,
        }));
        return;
      }

      const currentSample = (sizeClusters?.samples || []).find(
        (item) => sizeClusterSampleKey(item) === key
      );
      if (!Array.isArray(currentSample?.taylorgram_series)) {
        await loadSizeClusters({ includeSeries: true });
      }
      setExpandedSizeClusterOverlays((current) => ({
        ...current,
        [key]: true,
      }));
    },
    [expandedSizeClusterOverlays, loadSizeClusters, sizeClusters]
  );

  useEffect(() => {
    appliedFocusRunIdRef.current = null;
    suppressedFocusRunIdRef.current = null;
    latestDerivedPreviewRequestIdRef.current = 0;
    nextDerivedPreviewRequestIdRef.current = 0;
    setSelectedPipeline(PREPARATION_PIPELINES.includes(normalizedInitialPipeline) ? normalizedInitialPipeline : 'size');
    setRecalculateOpen(false);
    setSelectedRunRow(null);
    setSelectedRunDetail(null);
    setSelectedRunDetailLoading(false);
    setSelectedRunDetailError(null);
    setDerivedDraft(null);
    setRunSubmissionError(null);
    setSubmittedDerivedExecutionRequest(null);
    setDerivedPublicationFailure(null);
    handledDerivedExecutionRef.current = null;
    setSizeClusters(null);
    setSizeClustersError(null);
    setSizeClustersLoading(false);
    setExpandedSizeClusterOverlays({});
  }, [batchId, initialSampleId, normalizedInitialPipeline]);

  useEffect(() => {
    if (!derivedDraft) {
      latestDerivedPreviewRequestIdRef.current = 0;
      nextDerivedPreviewRequestIdRef.current = 0;
    }
  }, [derivedDraft]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const fetchAll = async () => {
      await loadBatch();
      await Promise.all([loadSamples(), loadRuns(), loadMeasurements()]);
      await loadAudit();
      if (active) setLoading(false);
    };
    if (batchId) {
      fetchAll();
    } else {
      setLoading(false);
    }
    return () => {
      active = false;
    };
  }, [batchId, loadBatch, loadSamples, loadRuns, loadMeasurements, loadAudit]);

  useEffect(() => {
    let active = true;
    setSizeSetupFields(emptyExperimentalFields());
    setSizeSetupError(null);
    if (!batchId) {
      setSizeSetupLoading(false);
      return () => {
        active = false;
      };
    }

    setSizeSetupLoading(true);
    fetchSizeSetupDefaults()
      .then((defaults) => {
        if (!active) return;
        const fields = mapSetupDetailToFields(defaults);
        const missing = findMissingExperimentalFields(fields);
        if (missing.length) {
          throw new Error('Size setup defaults response is incomplete.');
        }
        setSizeSetupFields(fields);
      })
      .catch((error) => {
        if (!active) return;
        setSizeSetupFields(emptyExperimentalFields());
        setSizeSetupError(
          extractApiError(error, 'Failed to load Size setup defaults.')
        );
      })
      .finally(() => {
        if (active) setSizeSetupLoading(false);
      });

    return () => {
      active = false;
    };
  }, [batchId]);

  useEffect(() => {
    if (!batch) return;
    const navigationKey = JSON.stringify([batchId, initialSampleId, normalizedInitialPipeline]);
    if (initializedNavigation.current === navigationKey) return;
    initializedNavigation.current = navigationKey;
    if (
      initialSampleId || (batch?.status === 'finalized' && normalizedInitialPipeline)
    ) {
      setActiveTab('runs');
    } else {
      setActiveTab('data');
    }
  }, [batch, batchId, initialSampleId, normalizedInitialPipeline]);

  useEffect(() => {
    if (
      activeTab === 'size-clusters' &&
      !sizeClusters &&
      !sizeClustersLoading &&
      !sizeClustersError
    ) {
      loadSizeClusters({ includeSeries: false });
    }
  }, [activeTab, loadSizeClusters, sizeClusters, sizeClustersError, sizeClustersLoading]);

  useEffect(() => {
    if (!focusRunId) {
      appliedFocusRunIdRef.current = null;
      suppressedFocusRunIdRef.current = null;
      return;
    }
    if (String(suppressedFocusRunIdRef.current) !== String(focusRunId)) {
      suppressedFocusRunIdRef.current = null;
    }
  }, [focusRunId]);

  useEffect(() => {
    if (!focusRunId || runs.length === 0) {
      logRunSelectionDebug('focusRunEffect', {
        action: !focusRunId ? 'skip-no-focus-run' : 'skip-no-runs',
      });
      return;
    }
    if (String(suppressedFocusRunIdRef.current) === String(focusRunId)) {
      logRunSelectionDebug('focusRunEffect', {
        action: 'skip-suppressed-focus-run',
      });
      return;
    }
    if (String(appliedFocusRunIdRef.current) === String(focusRunId)) {
      logRunSelectionDebug('focusRunEffect', {
        action: 'skip-already-applied-focus-run',
      });
      return;
    }
    setActiveTab('runs');
    const run = runs.find((item) => String(item.id) === String(focusRunId));
    logRunSelectionDebug('focusRunEffect', {
      action: run ? 'select-focus-run' : 'focus-run-not-found',
      targetRunId: run?.id ?? null,
    });
    if (run) {
      appliedFocusRunIdRef.current = String(focusRunId);
      setSelectedRunRow(run);
    }
  }, [focusRunId, logRunSelectionDebug, runs]);

  useEffect(() => {
    if (!selectedRunRow?.id) return;
    const refreshed = runs.find((item) => String(item.id) === String(selectedRunRow.id));
    logRunSelectionDebug('runsSyncEffect', {
      action: !refreshed
        ? 'clear-missing-selected-run'
        : refreshed !== selectedRunRow
          ? 'refresh-selected-run-reference'
          : 'keep-selected-run',
      targetRunId: selectedRunRow.id,
      refreshedRunId: refreshed?.id ?? null,
    });
    if (!refreshed) {
      setSelectedRunRow(null);
      return;
    }
    if (refreshed !== selectedRunRow) {
      setSelectedRunRow(refreshed);
    }
  }, [logRunSelectionDebug, runs, selectedRunRow]);

  useEffect(() => {
    if (!batchId || !selectedRunRow?.id) {
      setSelectedRunDetail(null);
      setSelectedRunDetailLoading(false);
      setSelectedRunDetailError(null);
      return undefined;
    }

    let active = true;
    // Refetch beyond run id so the saved-detail pane stays in sync when the same run
    // transitions operational state in the lightweight list (for example running ->
    // completed/failed) without requiring the user to reselect it.
    const currentDetailRunId = selectedRunDetailRef.current?.identity?.run_id ?? null;
    if (String(currentDetailRunId) !== String(selectedRunRow.id)) {
      setSelectedRunDetail(null);
    }
    setSelectedRunDetailLoading(true);
    setSelectedRunDetailError(null);

    fetchBatchRunSavedDetail(batchId, selectedRunRow.id)
      .then((detail) => {
        if (!active) return;
        setSelectedRunDetail(detail);
        setSelectedRunDetailLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        setSelectedRunDetail(null);
        setSelectedRunDetailLoading(false);
        setSelectedRunDetailError(extractApiError(error, 'Failed to load saved run detail.'));
      });

    return () => {
      active = false;
    };
  }, [
    batchId,
    selectedRunRow?.id,
    selectedRunRow?.status,
  ]);

  useEffect(() => {
    setDerivedDraft((current) => {
      const nextDraft =
        !current
          ? current
          : !selectedRunRow?.id
            ? null
            : String(current.sourceRunId) === String(selectedRunRow.id)
              ? current
              : null;
      logRunSelectionDebug('selectedRunDerivedDraftCleanupEffect', {
        action:
          !current
            ? 'no-draft'
            : !selectedRunRow?.id
              ? 'clear-draft-no-selected-run'
              : String(current.sourceRunId) === String(selectedRunRow.id)
                ? 'keep-draft'
                : 'clear-draft-selected-run-changed',
        effectSelectedRunId: selectedRunRow?.id ?? null,
        currentDraftSourceRunId: current?.sourceRunId ?? null,
      });
      return nextDraft;
    });
  }, [logRunSelectionDebug, selectedRunRow?.id]);

  const handleSelectRun = useCallback((run) => {
    logRunSelectionDebug('handleSelectRun', {
      targetRunId: run?.id ?? null,
    });
    setDerivedDraft(null);
    setSelectedRunRow(run);
  }, [logRunSelectionDebug]);

  useEffect(() => {
    const executionRequestId = derivedExecutionRequestId;
    const requestStatus = derivedExecutionRequestStatus;
    const planItemStatus = derivedExecutionStatus;

    if (!executionRequestId) {
      return undefined;
    }

    if (requestStatus === 'failed' || planItemStatus === 'failed') {
      const terminalKey = `${executionRequestId}:failed`;
      if (handledDerivedExecutionRef.current !== terminalKey) {
        handledDerivedExecutionRef.current = terminalKey;
        setDerivedDraft((current) =>
          current ? { ...current, saveLoading: false } : current
        );
      }
      return undefined;
    }

    if (requestStatus !== 'completed' && planItemStatus !== 'completed') {
      return undefined;
    }

    const terminalKey = `${executionRequestId}:completed`;
    if (handledDerivedExecutionRef.current === terminalKey) {
      return undefined;
    }
    handledDerivedExecutionRef.current = terminalKey;

    const workflowRunId = completedDerivedWorkflowRunId;
    if (!workflowRunId) {
      setDerivedDraft((current) =>
        current
          ? {
              ...current,
              saveLoading: false,
              saveError:
                'Derived execution completed without a workflow_run_id in its durable status.',
            }
          : current
      );
      return undefined;
    }

    let active = true;
    const openCompletedRun = async () => {
      try {
        const refreshedRuns = await loadRuns();
        if (!active) return;
        const savedRun = (refreshedRuns || []).find(
          (run) => String(run.id) === String(workflowRunId)
        );
        if (!savedRun) {
          throw new Error('Completed derived run was not found after refreshing runs.');
        }

        logRunSelectionDebug('derivedExecution:openCompletedRun', {
          executionRequestId,
          savedRunId: workflowRunId,
        });
        suppressedFocusRunIdRef.current = focusRunId ? String(focusRunId) : null;
        setSubmittedDerivedExecutionRequest(null);
        setDerivedPublicationFailure(null);
        setActiveTab('runs');
        handleSelectRun(savedRun);
      } catch (error) {
        if (!active) return;
        setDerivedDraft((current) =>
          current
            ? {
                ...current,
                saveLoading: false,
                saveError: extractApiError(
                  error,
                  'Failed to open the completed derived run.'
                ),
              }
            : current
        );
      }
    };
    void openCompletedRun();

    return () => {
      active = false;
    };
  }, [
    completedDerivedWorkflowRunId,
    derivedExecutionRequestId,
    derivedExecutionRequestStatus,
    derivedExecutionStatus,
    focusRunId,
    handleSelectRun,
    loadRuns,
    logRunSelectionDebug,
  ]);

  const handleOpenRunById = useCallback(
    (runId) => {
      const match = runs.find((run) => String(run.id) === String(runId));
      if (match) {
        handleSelectRun(match);
      }
    },
    [handleSelectRun, runs]
  );

  const handleOpenActiveRunById = useCallback(
    async (runId) => {
      let match = (runsRef.current || []).find(
        (run) => String(run.id) === String(runId)
      );
      if (!match) {
        try {
          const refreshedRuns = await loadRuns();
          match = (refreshedRuns || []).find(
            (run) => String(run.id) === String(runId)
          );
        } catch (error) {
          setRunSubmissionError(
            extractApiError(error, 'Failed to open the completed run.')
          );
          return;
        }
      }
      if (!match) {
        setRunSubmissionError(
          'The completed run is not available in the saved-runs list yet.'
        );
        return;
      }
      setRunSubmissionError(null);
      setActiveTab('runs');
      handleSelectRun(match);
    },
    [handleSelectRun, loadRuns]
  );

  const handleStartDerivedExploration = useCallback(() => {
    const canExplore = Boolean(selectedRunSeed) && canExploreDerivedSeed(selectedRunSeed);
    logRunSelectionDebug('handleStartDerivedExploration', {
      action: canExplore ? 'seed-derived-draft' : 'skip-cannot-explore',
      targetRunId: selectedRunRow?.id ?? null,
    });
    if (!selectedRunSeed || !canExploreDerivedSeed(selectedRunSeed)) {
      return;
    }
    setDerivedPublicationFailure(null);
    setDerivedDraft(seedDerivedDraftFromSeed(selectedRunSeed));
  }, [logRunSelectionDebug, selectedRunRow?.id, selectedRunSeed]);

  const handleDerivedParamDraftChange = useCallback((nextParamDraft) => {
    setDerivedDraft((current) => {
      if (!current) return current;
      const nextDraft = {
        ...current,
        paramDraft: nextParamDraft,
        previewStatus: DERIVED_PREVIEW_STATUS.IDLE,
        previewError: null,
        saveError: null,
        rerunReasonCode: current.rerunReasonTouched
          ? current.rerunReasonCode
          : getSuggestedRerunReasonCodeForSeed(
              current.sourceRunSeed,
              nextParamDraft,
              current.requestedTraceDraft
            ),
      };
      return {
        ...nextDraft,
        previewStatus: getPreviewStatusAfterDerivedDraftChange(nextDraft),
      };
    });
  }, []);

  const handleDerivedRequestedTraceDraftChange = useCallback((nextRequestedTraceDraft) => {
    setDerivedDraft((current) => {
      if (!current) return current;
      const nextDraft = {
        ...current,
        requestedTraceDraft: nextRequestedTraceDraft,
        previewStatus: DERIVED_PREVIEW_STATUS.IDLE,
        previewError: null,
        saveError: null,
        rerunReasonCode: current.rerunReasonTouched
          ? current.rerunReasonCode
          : getSuggestedRerunReasonCodeForSeed(
              current.sourceRunSeed,
              current.paramDraft,
              nextRequestedTraceDraft
            ),
      };
      return {
        ...nextDraft,
        previewStatus: getPreviewStatusAfterDerivedDraftChange(nextDraft),
      };
    });
  }, []);

  const handleDerivedReasonCodeChange = useCallback((nextCode) => {
    setDerivedDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        rerunReasonCode: nextCode,
        rerunReasonTouched: true,
        saveError: null,
      };
    });
  }, []);

  const handleDerivedReasonTextChange = useCallback((nextText) => {
    setDerivedDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        rerunReasonText: nextText,
        saveError: null,
      };
    });
  }, []);

  const runDerivedPreviewForDraft = useCallback(
    async (draftSnapshot) => {
      if (!draftSnapshot || !hasDerivedDraftChanges(draftSnapshot)) {
        return;
      }

      const requestSignature = getDerivedPreviewSignature(draftSnapshot);
      const requestId = nextDerivedPreviewRequestIdRef.current + 1;
      nextDerivedPreviewRequestIdRef.current = requestId;
      latestDerivedPreviewRequestIdRef.current = requestId;

      setDerivedDraft((current) => {
        if (!current || getDerivedPreviewSignature(current) !== requestSignature) {
          return current;
        }
        return {
          ...current,
          previewStatus: DERIVED_PREVIEW_STATUS.LOADING,
          previewError: null,
          saveError: null,
        };
      });

      try {
        const preview = await previewDerivedRun(
          batchId,
          buildDerivedPreviewPayload(draftSnapshot)
        );
        setDerivedDraft((current) => {
          if (
            !shouldApplyDerivedPreviewResponse(
              current,
              requestId,
              latestDerivedPreviewRequestIdRef.current,
              requestSignature
            )
          ) {
            return current;
          }
          return {
            ...current,
            previewResponse: preview,
            previewStatus: DERIVED_PREVIEW_STATUS.VALID,
            previewedSignature: requestSignature,
            previewError: null,
          };
        });
      } catch (error) {
        setDerivedDraft((current) => {
          if (
            !shouldApplyDerivedPreviewResponse(
              current,
              requestId,
              latestDerivedPreviewRequestIdRef.current,
              requestSignature
            )
          ) {
            return current;
          }
          return {
            ...current,
            previewStatus: DERIVED_PREVIEW_STATUS.ERROR,
            previewError: extractApiError(
              error,
              'Failed to run exploratory preview.'
            ),
          };
        });
      }
    },
    [batchId]
  );

  const derivedPreviewSignature = useMemo(
    () => (derivedDraft ? getDerivedPreviewSignature(derivedDraft) : null),
    [derivedDraft]
  );
  const derivedDraftHasChanges = useMemo(
    () => (derivedDraft ? hasDerivedDraftChanges(derivedDraft) : false),
    [derivedDraft]
  );
  const derivedDraftHasFreshPreview = useMemo(
    () => (derivedDraft ? hasFreshDerivedPreview(derivedDraft) : false),
    [derivedDraft]
  );

  // TEMP: manual preview only for now to avoid auto-preview firing on incomplete transient numeric edits.
  /*
  useEffect(() => {
    if (!derivedDraft?.sourceRunId || !derivedDraftHasChanges || !derivedPreviewSignature) {
      return undefined;
    }
    if (
      derivedDraft.saveLoading ||
      derivedDraftHasFreshPreview ||
      derivedDraft.previewStatus === DERIVED_PREVIEW_STATUS.LOADING ||
      derivedDraft.previewStatus === DERIVED_PREVIEW_STATUS.ERROR
    ) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      const currentDraft = derivedDraftRef.current;
      if (
        !currentDraft ||
        currentDraft.saveLoading ||
        !hasDerivedDraftChanges(currentDraft) ||
        hasFreshDerivedPreview(currentDraft)
      ) {
        return;
      }
      void runDerivedPreviewForDraft(currentDraft);
    }, DERIVED_PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    derivedDraft?.saveLoading,
    derivedDraft?.sourceRunId,
    derivedDraft?.previewStatus,
    derivedDraftHasChanges,
    derivedDraftHasFreshPreview,
    derivedPreviewSignature,
    runDerivedPreviewForDraft,
  ]);
  */

  const handleDerivedPreview = useCallback(async () => {
    const currentDraft = derivedDraftRef.current;
    if (!currentDraft) {
      return;
    }
    await runDerivedPreviewForDraft(currentDraft);
  }, [runDerivedPreviewForDraft]);

  const handleSaveDerivedRun = useCallback(async () => {
    const currentDraft = derivedDraftRef.current;
    if (!currentDraft) {
      return;
    }
    logRunSelectionDebug('handleSaveDerivedRun:start', {
      savedRunId: null,
    });

    const rerunTextRequired = ['INVESTIGATION', 'METHOD_UPDATE', 'OTHER'].includes(
      currentDraft.rerunReasonCode
    );

    if (!hasFreshDerivedPreview(currentDraft)) {
      setDerivedDraft((current) =>
        current
          ? {
              ...current,
              saveError:
                current.previewStatus === DERIVED_PREVIEW_STATUS.LOADING
                  ? 'Wait for the current preview to finish before saving.'
                  : current.previewStatus === DERIVED_PREVIEW_STATUS.STALE
                    ? 'Preview the latest draft before saving a derived run.'
                    : 'Preview the current draft before saving a derived run.',
            }
          : current
      );
      return;
    }

    if (rerunTextRequired && !currentDraft.rerunReasonText.trim()) {
      setDerivedDraft((current) =>
        current
          ? {
              ...current,
              saveError: 'rerun_reason_text is required for this rerun_reason_code.',
            }
          : current
      );
      return;
    }

    setSubmittedDerivedExecutionRequest(null);
    setDerivedPublicationFailure(null);
    handledDerivedExecutionRef.current = null;
    setDerivedDraft((current) =>
      current
        ? {
            ...current,
            saveLoading: true,
            saveError: null,
          }
        : current
    );

    try {
      const submission = retainExecutionRequestSubmission({
        batchId,
        requestType: 'derived-run',
        payload: buildDerivedSavePayload(currentDraft, null),
        createClientRequestId: generateClientRequestId,
      });
      const response = await saveDerivedRun(
        batchId,
        submission.payload
      );
      const executionRequestId = response?.execution_request_id;
      if (!executionRequestId) {
        throw new Error(
          'Derived execution response did not include an execution_request_id.'
        );
      }
      clearRetainedExecutionRequestSubmission({
        batchId,
        requestType: 'derived-run',
      });
      setDerivedPublicationFailure(null);
      logRunSelectionDebug('handleSaveDerivedRun:scheduled', {
        executionRequestId,
      });
      setSubmittedDerivedExecutionRequest(response);
    } catch (error) {
      const statusCode = error?.response?.status;
      const responsePayload = error?.response?.data;
      if (statusCode === 503 && responsePayload?.execution_request_id) {
        clearRetainedExecutionRequestSubmission({
          batchId,
          requestType: 'derived-run',
        });
        setSubmittedDerivedExecutionRequest(responsePayload);
        setDerivedPublicationFailure({
          status: responsePayload.publication_status || 'complete_failure',
          failedAttemptIds: responsePayload.failed_attempt_ids || [],
        });
        setDerivedDraft((current) =>
          current
            ? {
                ...current,
                saveLoading: false,
                saveError: null,
              }
            : current
        );
        return;
      }
      setDerivedDraft((current) =>
        current
          ? {
              ...current,
              saveLoading: false,
              saveError: extractApiError(error, 'Failed to save derived run.'),
            }
          : current
      );
    }
  }, [batchId, logRunSelectionDebug]);

  const handleCancelDerivedExploration = useCallback(() => {
    if (derivedExecutionIsActive) return;
    setSubmittedDerivedExecutionRequest(null);
    setDerivedPublicationFailure(null);
    handledDerivedExecutionRef.current = null;
    setDerivedDraft(null);
  }, [derivedExecutionIsActive]);

  const handleMetaSave = async (event) => {
    event.preventDefault();
    setSavingMeta(true);
    try {
      await updateBatch(batchId, metadataFields);
      await loadBatch();
      setShowMetaEditor(false);
    } finally {
      setSavingMeta(false);
    }
  };

  const filteredSamples = useMemo(() => samples.filter((sample) =>
    !sampleFilters.search || sample.name?.toLowerCase().includes(sampleFilters.search.toLowerCase())
  ), [samples, sampleFilters.search]);

  const measurementsById = useMemo(() => {
    const map = new Map();
    measurements.forEach((m) => map.set(m.id, m));
    return map;
  }, [measurements]);

  const measurementLabelById = useCallback(
    (measurementId) => {
      const measurement = allMeasurements.find((item) => item.id === Number(measurementId));
      if (!measurement) return `Measurement #${measurementId}`;
      const role = measurement.file_role ? ` (${measurement.file_role})` : '';
      return `${measurement.filename || `Measurement #${measurementId}`}${role}`;
    },
    [allMeasurements]
  );

  const sampleLabelById = useCallback(
    (sampleId) => {
      const sample = samples.find(
        (item) => String(item.id) === String(sampleId)
      );
      return sample?.name || sample?.sample_name || `Sample ${sampleId}`;
    },
    [samples]
  );

  const handleCompositionChange = async (sampleId, composition) => {
    const sample = samples.find((item) => item.id === sampleId);
    if (!sample?.batch_sample_id) {
      throw new Error(`Missing batch_sample_id for sample ${sampleId}`);
    }
    await confirmSampleComposition(batchId, sample.batch_sample_id, composition);
    await Promise.all([loadSamples(), loadAudit()]);
  };

  const executeImport = useCallback(
    async (files, pipeline, instrument, uploadScope) => {
      if (!files.length || !batchId) return false;
      if (uploadScope?.batchId !== batchId) {
        setPendingUploadPreview(null);
        setPreparationNotice('The upload belongs to another Batch. Review this workspace and select the files again.');
        return false;
      }
      const normalizedInstrument = String(instrument || '').trim();
      if (!normalizedInstrument) {
        if (pipeline === 'encapsulation') {
          setEeErrors({ detail: 'Instrument for this upload is required.' });
        } else {
          setSizeUploadError('Instrument for this upload is required.');
        }
        return false;
      }
      let sizeSetupInput;
      if (pipeline === 'size') {
        const missing = findMissingExperimentalFields(sizeSetupFields);
        if (missing.length) {
          setSizeUploadError(formatMissingMessage(missing));
          return false;
        }
        sizeSetupInput = buildSetupPayload(sizeSetupFields);
        setSizeUploadError(null);
      }
      setImportErrors([]);
      if (pipeline === 'encapsulation') {
        setEeUploadState((prev) => ({ ...prev, uploading: true }));
      } else {
        setSizeUploadState((prev) => ({ ...prev, uploading: true }));
      }
      try {
        const result = await importBatchSamples(
          uploadScope.batchId,
          files,
          pipeline,
          normalizedInstrument,
          sizeSetupInput,
          uploadScope.cycle_number
        );
        await refreshAfterPreparationMutation();
        if (pipeline === selectedPipeline) {
          setGroupingDraft(null);
          setExpandedGroupingClusters({});
        }
        setImportErrors(result?.errors || []);
        const timestamp = new Date();
        if (pipeline === 'encapsulation') {
          setEeUploadState({
            uploading: false,
            lastCount: result?.measurements?.length ?? files.length,
            lastUploadedAt: timestamp,
          });
          setEeErrors(null);
        } else {
          setSizeUploadState({
            uploading: false,
            lastCount: result?.measurements?.length ?? files.length,
            lastUploadedAt: timestamp,
          });
          setSizeUploadError(null);
        }
        return true;
      } catch (error) {
        if (error?.response?.status === 409) {
          setPendingUploadPreview(null);
          let refreshFailed = false;
          try { await refreshPreparation(); }
          catch (_) { refreshFailed = true; requirePreparationReload(); }
          const message = `${pipeline === 'size' ? 'Size' : 'Encapsulation'} upload was not saved: preparation changed. Review the current cycle and select the files again. No upload was retried.${refreshFailed ? ' Refresh failed; reload the workspace before uploading.' : ''}`;
          setPreparationNotice(message);
          if (pipeline === 'encapsulation') {
            setEeErrors({ detail: message });
            setEeUploadState((prev) => ({ ...prev, uploading: false }));
          } else {
            setSizeUploadError(message);
            setSizeUploadState((prev) => ({ ...prev, uploading: false }));
          }
          return false;
        }
        if (pipeline === 'encapsulation') {
          const data = error?.response?.data || {};
          setEeErrors({
            detail:
              data.detail ||
              'Encapsulation files must include matching _TOTAL and _FREE pairs.',
            invalid_files: data.invalid_files || [],
            missing_pairs: data.missing_pairs || [],
          });
          setEeUploadState((prev) => ({ ...prev, uploading: false }));
        } else {
          setSizeUploadError(
            extractApiError(error, 'Size upload failed. Please try again.')
          );
          setSizeUploadState((prev) => ({ ...prev, uploading: false }));
        }
        return false;
      }
    },
    [batchId, refreshAfterPreparationMutation, refreshPreparation, requirePreparationReload, selectedPipeline, sizeSetupFields, setGroupingDraft, setExpandedGroupingClusters, setImportErrors, setPreparationNotice]
  );

  const handleImport = useCallback(
    (files, pipeline) => {
      if (!files.length || !batchId) return;
      if (!batch || batch.status !== 'draft' || batch.status === 'void') {
        return;
      }
      if (pipeline === 'size') {
        if (!sizeUploadInstrument.trim()) {
          setSizeUploadError('Instrument for this upload is required.');
          return;
        }
        if (sizeSetupLoading || sizeSetupError) return;
        const missing = findMissingExperimentalFields(sizeSetupFields);
        if (missing.length) {
          setSizeUploadError(formatMissingMessage(missing));
          return;
        }
        setSizeUploadError(null);
      } else if (!encapsulationUploadInstrument.trim()) {
        setEeErrors({ detail: 'Instrument for this upload is required.' });
        return;
      }
      const scope = batch.pipeline_preparation?.find((item) => item.pipeline === pipeline);
      if (!Number.isInteger(scope?.current_cycle_number) || scope.current_cycle_number < 1 ||
          scope.preparation_permitted !== true) return;
      setPendingUploadPreview({
        batchId,
        cycle_number: scope.current_cycle_number,
        files,
        pipeline,
        instrument:
          pipeline === 'size'
            ? sizeUploadInstrument.trim()
            : encapsulationUploadInstrument.trim(),
      });
    },
    [
      batch,
      batchId,
      encapsulationUploadInstrument,
      sizeSetupError,
      sizeSetupFields,
      sizeSetupLoading,
      sizeUploadInstrument,
    ]
  );

  const handleSizeSetupChange = useCallback((field, value) => {
    setSizeSetupFields((current) => ({ ...current, [field]: value }));
    setSizeUploadError(null);
  }, []);

  const isDraft = batch?.status === 'draft';
  const isVoid = batch?.status === 'void';
  const isFinalized = batch?.status === 'finalized';
  const noProject = !batch?.project;
  const preparationBlocked = preparationReloadRequired || !isDraft || preparationState?.preparation_permitted !== true;
  const importsDisabled = preparationBlocked;
  const confirmedSampleIds = new Set(samples.map((sample) => String(sample.id)));
  const hasPersistedGroupingDecisions = measurements.some(
    (measurement) =>
      measurement.is_unclusterable ||
      (measurement.sample_id != null &&
        confirmedSampleIds.has(String(measurement.sample_id)))
  );
  const missingSizeSetupFields = findMissingExperimentalFields(sizeSetupFields);
  const sizeSetupReady =
    !sizeSetupLoading && !sizeSetupError && missingSizeSetupFields.length === 0;
  const sizeImportDisabled =
    importsDisabled || !sizeSetupReady || !sizeUploadInstrument.trim();
  const encapsulationImportDisabled =
    importsDisabled || !encapsulationUploadInstrument.trim();
  const resolveLinkedSampleName = useCallback(
    (measurement) => {
      if (!measurement?.sample_id) return '—';
      if (measurement.linked_sample_name) return measurement.linked_sample_name;
      const fromBatchSamples = samples.find((s) => s.id === measurement.sample_id);
      return fromBatchSamples ? fromBatchSamples.name : `Sample #${measurement.sample_id}`;
    },
    [samples]
  );
  const groupedMeasurements = useMemo(() => {
    const grouped = new Map();
    measurements.forEach((measurement) => {
      if (!measurement.sample_id) return;
      if (!grouped.has(measurement.sample_id)) {
        grouped.set(measurement.sample_id, {
          sample_id: measurement.sample_id,
          sample_name: resolveLinkedSampleName(measurement),
          items: [],
        });
      }
      grouped.get(measurement.sample_id).items.push(measurement);
    });
    return Array.from(grouped.values()).sort((a, b) =>
      a.sample_name.localeCompare(b.sample_name)
    );
  }, [measurements, resolveLinkedSampleName]);
  const groupingValidation = useMemo(() => {
    if (!groupingDraft) {
      return {
        canConfirm: false,
        emptyNameClusters: [],
        emptyClusters: [],
        duplicateIds: [],
      };
    }
    const emptyNameClusters = [];
    const emptyClusters = [];
    const duplicateIds = [];
    const seen = new Set();
    groupingDraft.clusters.forEach((cluster, idx) => {
      if (!cluster.sample_name.trim()) emptyNameClusters.push(idx);
      if (!cluster.measurement_ids.length) emptyClusters.push(idx);
      cluster.measurement_ids.forEach((mid) => {
        if (seen.has(mid)) duplicateIds.push(mid);
        else seen.add(mid);
      });
    });
    const canConfirm =
      (groupingDraft.clusters.length > 0 ||
        groupingDraft.unclusterable.length > 0) &&
      emptyNameClusters.length === 0 &&
      emptyClusters.length === 0 &&
      duplicateIds.length === 0;
    return { canConfirm, emptyNameClusters, emptyClusters, duplicateIds };
  }, [groupingDraft]);
  const moveMeasurementTo = useCallback((measurementId, targetType, targetClusterIdx) => {
    const parsedId = Number(measurementId);
    if (!Number.isInteger(parsedId)) return;
    setGroupingDraft((prev) => {
      if (!prev) return prev;
      const clusters = prev.clusters.map((cluster) => ({
        ...cluster,
        measurement_ids: [...cluster.measurement_ids],
      }));
      let unclusterable = [...prev.unclusterable];
      clusters.forEach((cluster) => {
        cluster.measurement_ids = cluster.measurement_ids.filter((id) => id !== parsedId);
      });
      unclusterable = unclusterable.filter((id) => id !== parsedId);
      if (
        targetType === 'cluster' &&
        Number.isInteger(targetClusterIdx) &&
        clusters[targetClusterIdx]
      ) {
        clusters[targetClusterIdx].measurement_ids.push(parsedId);
      } else {
        unclusterable.push(parsedId);
      }
      return normalizeGroupingDraft({ ...prev, clusters, unclusterable });
    });
  }, [setGroupingDraft]);
  const addGroupingCluster = useCallback(() => {
    setGroupingDraft((prev) =>
      normalizeGroupingDraft({
        ...prev,
        clusters: [
          ...(prev?.clusters || []),
          { sample_name: '', measurement_ids: [] },
        ],
        unclusterable: prev?.unclusterable || [],
      })
    );
  }, [setGroupingDraft]);
  const renameGroupingCluster = useCallback((clusterIdx, sampleName) => {
    setGroupingDraft((prev) => {
      if (!prev || !prev.clusters[clusterIdx]) return prev;
      const clusters = prev.clusters.map((cluster, idx) =>
        idx === clusterIdx ? { ...cluster, sample_name: sampleName } : cluster
      );
      return { ...prev, clusters };
    });
  }, [setGroupingDraft]);
  const removeGroupingCluster = useCallback((clusterIdx) => {
    setGroupingDraft((prev) => {
      if (!prev || !prev.clusters[clusterIdx]) return prev;
      const removed = prev.clusters[clusterIdx];
      const clusters = prev.clusters.filter((_, idx) => idx !== clusterIdx);
      const unclusterable = [...prev.unclusterable, ...removed.measurement_ids];
      return normalizeGroupingDraft({ ...prev, clusters, unclusterable });
    });
  }, [setGroupingDraft]);

  if (!batchId) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Select a batch to view details.
      </div>
    );
  }

  if (loading || !batch) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Loading batch…
      </div>
    );
  }

  const renderImportsSection = () => {
    const sizeMeasurementCount = measurements.filter(
      (measurement) => measurement.pipeline === 'size'
    ).length;
    const encapsulationMeasurementCount = measurements.filter(
      (measurement) => measurement.pipeline === 'encapsulation'
    ).length;

    return (
      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-slate-900">Pipeline uploads</h2>
        <div className="grid gap-4 xl:grid-cols-2">
          <div hidden={selectedPipeline !== 'size'}><UploadPanel
            icon="📏"
            title="Size"
            description="Upload instrument files intended for Size analysis."
            helper="Files uploaded here are assigned to the Size pipeline."
            uploading={sizeUploadState.uploading}
            summary={`${sizeMeasurementCount} Size measurement${
              sizeMeasurementCount === 1 ? '' : 's'
            } uploaded`}
            lastUploadedAt={sizeUploadState.lastUploadedAt}
            onFiles={(files) => handleImport(files, 'size')}
            error={sizeUploadError}
            disabled={sizeImportDisabled}
            disabledMessage={
              importsDisabled
                ? preparationState?.blocking_reason?.detail || 'Preparation state unavailable or protected.'
                : !sizeUploadInstrument.trim()
                  ? 'Select or enter the instrument for this upload.'
                : sizeSetupLoading
                  ? 'Loading Size setup defaults…'
                  : sizeSetupError
                    ? 'Size setup defaults must load before upload.'
                    : 'Complete all eight Size setup fields before upload.'
            }
          >
            <label className="mb-4 block text-sm">
              <span className="font-medium text-slate-700">
                Instrument for this upload
              </span>
              <input
                aria-label="Size instrument for this upload"
                className="mt-1 w-full rounded border border-slate-300 p-2"
                value={sizeUploadInstrument}
                disabled={importsDisabled}
                maxLength={255}
                placeholder="Enter instrument name or identifier"
                onChange={(event) => {
                  setSizeUploadInstrument(event.target.value);
                  setSizeUploadError(null);
                }}
              />
            </label>
            {sizeSetupLoading ? (
              <p className="mt-4 text-sm text-slate-500" role="status">
                Loading Size setup defaults…
              </p>
            ) : sizeSetupError ? (
              <p
                className="mt-4 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
                role="alert"
              >
                {sizeSetupError}
              </p>
            ) : (
              <>
                <SizeSetupForm
                  values={sizeSetupFields}
                  onChange={handleSizeSetupChange}
                  disabled={importsDisabled}
                />
                {missingSizeSetupFields.length > 0 && (
                  <p
                    className="mt-2 text-xs text-rose-600"
                    role="alert"
                  >
                    {formatMissingMessage(missingSizeSetupFields)}
                  </p>
                )}
              </>
            )}
          </UploadPanel></div>
          <div hidden={selectedPipeline !== 'encapsulation'}><UploadPanel
            icon="🧪"
            title="Encapsulation"
            description="Upload instrument files intended for Encapsulation analysis."
            helper="Roles are detected from filename or metadata and can be paired later."
            uploading={eeUploadState.uploading}
            summary={`${encapsulationMeasurementCount} Encapsulation measurement${
              encapsulationMeasurementCount === 1 ? '' : 's'
            } uploaded`}
            lastUploadedAt={eeUploadState.lastUploadedAt}
            onFiles={(files) => handleImport(files, 'encapsulation')}
            error={eeErrors?.detail}
            disabled={encapsulationImportDisabled}
            disabledMessage={
              importsDisabled
                ? preparationState?.blocking_reason?.detail || 'Preparation state unavailable or protected.'
                : 'Select or enter the instrument for this upload.'
            }
          >
            <label className="mb-4 block text-sm">
              <span className="font-medium text-slate-700">
                Instrument for this upload
              </span>
              <input
                aria-label="Encapsulation instrument for this upload"
                className="mt-1 w-full rounded border border-slate-300 p-2"
                value={encapsulationUploadInstrument}
                disabled={importsDisabled}
                maxLength={255}
                placeholder="Enter instrument name or identifier"
                onChange={(event) => {
                  setEncapsulationUploadInstrument(event.target.value);
                  setEeErrors(null);
                }}
              />
            </label>
          </UploadPanel></div>
        </div>
      </section>
    );
  };

  const renderSamplesSection = () => (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-slate-900">Samples</h2>
      <SamplesPanel
        samples={filteredSamples}
        totalSamples={samples.length}
        groupedMeasurements={groupedMeasurements}
        sampleFilters={sampleFilters}
        setSampleFilters={setSampleFilters}
        showCompositionActions={activeTab === 'grouping' && measurements.length > 0}
        canEditComposition={isDraft && !preparationBlocked}
        onConfirmComposition={setEditingSample}
      />
    </section>
  );

  const renderRunsSection = () => (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-slate-900">Runs</h2>
      {selectedRunRow ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={() => handleSelectRun(null)}
              disabled={derivedExecutionIsActive}
              className="text-blue-600 hover:underline text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              ← Back to runs list
            </button>
            {!derivedDraft && (
              <button
                type="button"
                onClick={handleStartDerivedExploration}
                disabled={!selectedRunSeed || !canExploreDerivedSeed(selectedRunSeed)}
                className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Explore changes
              </button>
            )}
          </div>
          {derivedDraft ? (
            <DerivedRunExplorer
              batchId={batchId}
              draft={derivedDraft}
              onChangeParamDraft={handleDerivedParamDraftChange}
              onChangeRequestedTraceDraft={handleDerivedRequestedTraceDraftChange}
              onChangeRerunReasonCode={handleDerivedReasonCodeChange}
              onChangeRerunReasonText={handleDerivedReasonTextChange}
              onPreview={handleDerivedPreview}
              onSave={handleSaveDerivedRun}
              onCancel={handleCancelDerivedExploration}
              executionRequest={activeDerivedExecutionRequest}
              publicationFailure={derivedPublicationFailure}
              pollingError={derivedExecutionPollingError}
            />
          ) : (
            <>
              {selectedRunDetail &&
                String(selectedRunDetail?.identity?.run_id) !== String(selectedRunRow?.id) && (
                  <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
                    Loading saved run detail…
                  </div>
                )}
              {selectedRunDetailLoading && !selectedRunDetail && (
                <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
                  Loading saved run detail…
                </div>
              )}
              {!selectedRunDetailLoading && selectedRunDetailError && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
                  {selectedRunDetailError}
                </div>
              )}
              {!selectedRunDetailLoading &&
                !selectedRunDetailError &&
                selectedRunDetail &&
                String(selectedRunDetail?.identity?.run_id) === String(selectedRunRow?.id) && (
                <RunAnalysisView
                  run={selectedRunDetail}
                  onOpenRun={handleOpenRunById}
                />
                )}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {runSubmissionError && (
            <div
              role="alert"
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            >
              {runSubmissionError}
            </div>
          )}
          <RunsPanel runs={runs} onSelectRun={handleSelectRun} />
        </div>
      )}
    </section>
  );

  const renderSizeClustersSection = () => {
    const sampleEntries = Array.isArray(sizeClusters?.samples) ? sizeClusters.samples : [];

    return (
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Size clusters</h2>
            <p className="text-sm text-slate-500">
              Batch-sample size component clusters computed from current official size runs.
            </p>
          </div>
          <button
            type="button"
            onClick={refreshSizeClusters}
            disabled={sizeClustersLoading}
            className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {sizeClustersLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {sizeClustersError && (
          <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {sizeClustersError}
          </div>
        )}

        {sizeClustersLoading && !sizeClusters && (
          <div className="rounded border border-slate-200 bg-white p-4 text-sm text-slate-500">
            Loading size clusters…
          </div>
        )}

        {!sizeClustersLoading && !sizeClustersError && sampleEntries.length === 0 && (
          <div className="rounded border border-slate-200 bg-white p-4 text-sm text-slate-500">
            No batch samples with size cluster data yet.
          </div>
        )}

        {sampleEntries.map((sample) => (
          <div
            key={sample.batch_sample_id || sample.sample_id}
            className="rounded border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {sample.sample_name || `Sample ${sample.sample_id}`}
                </h3>
                <p className="text-xs text-slate-500">
                  Batch sample #{sample.batch_sample_id} • {sample.n_measurements || 0} measurement{sample.n_measurements === 1 ? '' : 's'} • {sample.n_components || 0} component{sample.n_components === 1 ? '' : 's'}
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                {sizeClusterStatusLabel(sample.clustering?.status)}
              </span>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => toggleSizeClusterOverlay(sample)}
                disabled={sizeClustersLoading}
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {expandedSizeClusterOverlays[sizeClusterSampleKey(sample)]
                  ? 'Hide overlay'
                  : Array.isArray(sample.taylorgram_series)
                    ? 'Show overlay'
                    : 'Load overlay'}
              </button>
              {expandedSizeClusterOverlays[sizeClusterSampleKey(sample)] && (
                Array.isArray(sample.taylorgram_series) && sample.taylorgram_series.length > 0 ? (
                  <SizeClusterOverlay sample={sample} />
                ) : (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    No overlay trace data is available for this batch sample.
                  </div>
                )
              )}
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <h4 className="text-sm font-semibold text-slate-800">Clusters</h4>
                <div className="mt-2 overflow-x-auto rounded border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Cluster</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">t0</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Mean Dh</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">RSD</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Components</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {(sample.clusters || []).map((cluster) => (
                        <tr key={cluster.cluster_id}>
                          <td className="px-2 py-2 font-medium text-slate-900">
                            {cluster.cluster_id}
                          </td>
                          <td className="px-2 py-2 text-slate-600">
                            {formatClusterNumber(cluster.center_t0, 2)}
                          </td>
                          <td className="px-2 py-2 text-slate-600">
                            {formatClusterNumber(cluster.mean_dh_nm, 2)} nm
                          </td>
                          <td className="px-2 py-2 text-slate-600">
                            {formatClusterNumber(cluster.rsd_percent, 1)}%
                          </td>
                          <td className="px-2 py-2 text-slate-600">
                            {cluster.n_components || 0} across {cluster.n_measurements || 0} measurement{cluster.n_measurements === 1 ? '' : 's'}
                          </td>
                        </tr>
                      ))}
                      {!(sample.clusters || []).length && (
                        <tr>
                          <td className="px-2 py-3 text-center text-slate-500" colSpan={5}>
                            No clusters for this batch sample.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">Source measurements</h4>
                  <p className="mt-1 text-xs text-slate-500">
                    {(sample.source_input_import_ids || []).join(', ') || 'None'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <ComponentTable
                title="Unclustered components"
                components={sample.unclustered_components || []}
                emptyText="No unclustered components."
              />
              <ComponentTable
                title="Excluded components"
                components={sample.excluded_components || []}
                emptyText="No excluded components."
                showReason
              />
            </div>
          </div>
        ))}
      </section>
    );
  };

  const renderAuditSection = () => (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Activity / Audit</h2>
        <p className="text-sm text-slate-500">Audit history for this batch.</p>
      </div>
      <ActivityPanel events={auditEvents} />
    </section>
  );

  const loadMeasurementPreviewForTrace = async (
    measurement, traceIdentity = null, expectedIdentity = traceIdentity,
    session = measurementPreviewSessionRef.current
  ) => {
    const requestId = ++measurementPreviewRequestIdRef.current;
    const isCurrent = () => measurementPreviewSessionRef.current === session &&
      measurementPreviewRequestIdRef.current === requestId;
    setLoadingMeasurementPreview(true);
    setMeasurementPreview(null);
    setMeasurementPreviewError(null);
    try {
      const previewData = await fetchMeasurementPreview(batchId, measurement.id, traceIdentity);
      if (!isCurrent()) return;
      const displayedIdentity = {
        trace_id: previewData?.effective_trace_id,
        wavelength_nm: previewData?.effective_wavelength_nm,
      };
      if (String(previewData?.measurement) !== String(measurement.id) ||
          !Object.prototype.hasOwnProperty.call(previewData || {}, 'effective_wavelength_nm') ||
          !displayedIdentity.trace_id ||
          (expectedIdentity && !measurementTraceIdentityMatches(displayedIdentity, expectedIdentity))) {
        throw new Error('Preview trace identity does not match the requested measurement and trace.');
      }
      setMeasurementPreview(previewData);
      setMeasurementTraceDraft(displayedIdentity);
    } catch (err) {
      if (isCurrent()) {
        setMeasurementPreview(null);
        setMeasurementPreviewError(
          err?.response?.data?.detail || err?.message || 'Failed to load preview.'
        );
      }
    } finally {
      if (isCurrent()) setLoadingMeasurementPreview(false);
    }
  };

  const openMeasurementPreviewModal = async (m) => {
    const session = ++measurementPreviewSessionRef.current;
    setMeasurementTraceDraftTouched(false);
    measurementPreviewRequestIdRef.current += 1;
    setPreviewMeasurement(m);
    setTracePreparationScope({ pipeline: selectedPipeline,
      cycle_number: preparationState?.current_cycle_number,
      preparation_revision: preparationState?.preparation_revision });
    setMeasurementPreview(null);
    setMeasurementTraces(null);
    setMeasurementTraceDraft(persistedMeasurementTraceIdentity(m));
    setSavingMeasurementTrace(false);
    setMeasurementPreviewError(null);
    setLoadingMeasurementPreview(false);
    setLoadingMeasurementTraces(true);
    try {
      const tracesData = await fetchMeasurementTraces(batchId, m.id);
      if (measurementPreviewSessionRef.current !== session) return;
      setMeasurementTraces(tracesData);
      const saved = persistedMeasurementTraceIdentity(tracesData);
      setMeasurementTraceDraft(saved);
      const expected = m.pipeline === 'size' ? saved || tracesData.recommended_trace : null;
      if (m.pipeline === 'size' && !expected) {
        setMeasurementPreviewError('No unambiguous automatic trace is available. Select an eligible trace to preview.');
        return;
      }
      setLoadingMeasurementTraces(false);
      await loadMeasurementPreviewForTrace(m, null, expected, session);
    } catch (err) {
      if (measurementPreviewSessionRef.current === session) {
        setMeasurementPreviewError(err?.response?.data?.detail || 'Failed to load traces.');
      }
    } finally {
      if (measurementPreviewSessionRef.current === session) setLoadingMeasurementTraces(false);
    }
  };

  const closeMeasurementPreviewModal = () => {
    measurementPreviewSessionRef.current += 1;
    measurementPreviewRequestIdRef.current += 1;
    setPreviewMeasurement(null);
    setMeasurementPreview(null);
    setMeasurementTraces(null);
    setMeasurementTraceDraft(null);
    setSavingMeasurementTrace(false);
    setMeasurementPreviewError(null);
    setLoadingMeasurementPreview(false);
    setLoadingMeasurementTraces(false);
  };

  const confirmMeasurementTraceChange = async (reset = false) => {
    if (!previewMeasurement || preparationBlocked || savingMeasurementTrace ||
        previewMeasurement.pipeline !== 'size' || !isCurrentMeasurement(previewMeasurement, preparationState) ||
        tracePreparationScope?.pipeline !== selectedPipeline) return;
    setSavingMeasurementTrace(true);
    try {
      const identity = reset ? { trace_id: null, wavelength_nm: null } : measurementTraceDraft;
      await selectMeasurementTrace(batchId, previewMeasurement.id, {
        trace_id: identity?.trace_id ?? null,
        ...(Object.prototype.hasOwnProperty.call(identity || {}, 'wavelength_nm') ? { wavelength_nm: identity.wavelength_nm } : {}),
        cycle_number: tracePreparationScope.cycle_number,
        preparation_revision: tracePreparationScope.preparation_revision,
      });
      await refreshAfterPreparationMutation();
      closeMeasurementPreviewModal();
    } catch (err) {
      if (await handlePreparationConflict(err)) closeMeasurementPreviewModal();
      else setMeasurementPreviewError(err?.response?.data?.detail || 'Failed to update trace.');
    } finally {
      setSavingMeasurementTrace(false);
    }
  };

  const previewMeasurementTrace = (trace) => {
    if (!previewMeasurement || preparationBlocked || savingMeasurementTrace) return;
    const traceIdentity = {
      trace_id: trace?.trace_id ?? null,
      ...(Object.prototype.hasOwnProperty.call(trace || {}, 'wavelength_nm') ? { wavelength_nm: trace.wavelength_nm } : {}),
    };
    setMeasurementTraceDraftTouched(true);
    setMeasurementTraceDraft(traceIdentity);
    loadMeasurementPreviewForTrace(previewMeasurement, traceIdentity);
  };

  const formatMeasurementTraceIdentityLabel = (trace) => {
    const parts = [];
    if (trace.trace_id) parts.push(trace.trace_id);
    if (trace.wavelength_nm !== null && trace.wavelength_nm !== undefined) {
      parts.push(`${trace.wavelength_nm} nm`);
    }
    if (trace.signal_unit_raw) parts.push(trace.signal_unit_raw);
    return parts.join(' \u2014 ') || 'Trace';
  };

  const renderMeasurementsSection = (compact = false) => {
    const mutateExclusion = async (mid, include) => {
      const measurement = measurements.find((item) => item.id === mid);
      if (preparationBlocked || measurementMutating || !isCurrentMeasurement(measurement || {}, preparationState)) return;
      const scope = { cycle_number: preparationState.current_cycle_number,
        preparation_revision: preparationState.preparation_revision };
      const reason = include ? '' : window.prompt('Reason for exclusion (optional):');
      if (reason === null) return;
      setMeasurementMutating(true);
      setPreparationNotice(null);
      try {
        if (include) await includeMeasurement(batchId, mid, scope);
        else await excludeMeasurement(batchId, mid, reason, scope);
        await refreshAfterPreparationMutation();
      } catch (error) {
        if (!(await handlePreparationConflict(error))) setPreparationNotice(extractApiError(error, 'Failed to update Measurement.'));
      } finally { setMeasurementMutating(false); }
    };
    const handleExclude = (mid) => mutateExclusion(mid, false);
    const handleInclude = (mid) => mutateExclusion(mid, true);

    const renderMeasurementAction = (m) => (m.status === 'accepted' && m.derived_status !== 'broken' && !preparationBlocked && !isVoid && (
                        m.is_excluded ? (
                          <button
                            className="text-xs font-semibold text-blue-700 hover:underline"
                            disabled={measurementMutating}
                            onClick={() => handleInclude(m.id)}
                          >
                            Include
                          </button>
                        ) : (
                          <button
                            className="text-xs font-semibold text-rose-700 hover:underline"
                            disabled={measurementMutating}
                            onClick={() => handleExclude(m.id)}
                          >
                            Exclude
                          </button>
                        )
                      ));

    const statusLabel = (m) => {
      if (m.status === 'rejected') return 'REJECTED';
      if (m.is_excluded) return 'EXCLUDED';
      if (m.is_unclusterable) return 'UNCLUSTERABLE';
      switch (m.derived_status) {
        case 'ready': return 'ACCEPTED';
        case 'ungrouped': return 'UNGROUPED';
        case 'needs_label': return 'NEEDS LABEL';
        case 'excluded': return 'EXCLUDED';
        case 'broken': return 'BROKEN';
        default: return m.derived_status || '\u2014';
      }
    };

    const traceCell = (m) => {
      if (m.pipeline !== 'size') return m.selected_trace_id || '\u2014';
      if (m.n_traces === 0 || m.derived_status === 'broken') return '\u2014';
      if (preparationBlocked) {
        const savedTrace =
          m.selected_trace_id ||
          (m.selected_wavelength_nm != null
            ? `${m.selected_wavelength_nm} nm`
            : '\u2014');
        return (
          <span>
            {savedTrace}{' '}
            <button
              className="text-xs text-blue-600 hover:underline"
              onClick={() => openMeasurementPreviewModal(m)}
            >
              Preview
            </button>
          </span>
        );
      }
      if (m.selected_trace_id) {
        return (
          <span>
            {m.selected_trace_id}{' '}
            <button
              className="text-xs text-blue-600 hover:underline"
              onClick={() => openMeasurementPreviewModal(m)}
            >
              (change)
            </button>
          </span>
        );
      }
      if (m.selected_wavelength_nm != null) {
        return (
          <span>
            {m.selected_wavelength_nm} nm{' '}
            <button
              className="text-xs text-blue-600 hover:underline"
              onClick={() => openMeasurementPreviewModal(m)}
            >
              (change)
            </button>
          </span>
        );
      }
      return (
        <button
          className="text-xs font-semibold text-blue-700 hover:underline"
          onClick={() => openMeasurementPreviewModal(m)}
        >
          Select trace
        </button>
      );
    };

    const traceOptions = Array.isArray(measurementTraces?.traces)
      ? measurementTraces.traces
      : [];
    const savedTraceIdentity = persistedMeasurementTraceIdentity(measurementTraces);
    const recommendedTrace = traceOptions.find((trace) => trace.recommended === true);
    const displayedTraceIdentity = measurementPreview ? {
      trace_id: measurementPreview.effective_trace_id,
      wavelength_nm: measurementPreview.effective_wavelength_nm,
    } : null;
    const traceSelectionChanged = measurementTraceDraftTouched && !measurementTraceIdentityMatches(
      measurementTraceDraft,
      savedTraceIdentity
    );
    const eligibleTraceOptions = traceOptions.filter(
      (trace) => trace.eligible !== false && trace.selectable !== false
    );
    const unavailableTraceOptions = traceOptions.filter(
      (trace) => trace.eligible === false || trace.selectable === false
    );

    const renderTraceAssessment = (trace, idx, interactive = false) => {
      const isDraftTrace = measurementTraceIdentityMatches(
        trace,
        measurementTraceDraft
      );
      const isSavedTrace = measurementTraceIdentityMatches(trace, savedTraceIdentity);
      const content = (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 break-all text-sm font-medium text-slate-800">
              {formatMeasurementTraceIdentityLabel(trace)}
            </span>
            <span className="flex flex-wrap justify-end gap-1.5">
              {trace.recommended && (
                <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                  Recommended
                </span>
              )}
              {isSavedTrace && (
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                  Saved
                </span>
              )}
              {measurementTraceIdentityMatches(trace, displayedTraceIdentity) && !isSavedTrace && (
                <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                  Previewing
                </span>
              )}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-600">
            {formatTraceScore(trace.score)} |{' '}
            {trace.eligible === true
              ? 'Eligible'
              : trace.eligible === false
                ? 'Ineligible'
                : 'Eligibility unavailable'}{' '}
            |{' '}
            {trace.selectable === true
              ? 'Selectable'
              : trace.selectable === false
                ? 'Not selectable'
                : 'Selectability unavailable'}
          </p>
          {Array.isArray(trace.rejection_reasons) &&
            trace.rejection_reasons.length > 0 && (
              <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs text-rose-700">
                {trace.rejection_reasons.map((reason, reasonIndex) => (
                  <li key={`${reason.code || 'reason'}-${reasonIndex}`}>
                    {reason.detail || reason.code}
                  </li>
                ))}
              </ul>
            )}
        </>
      );
      const key = `assessment-${encodeMeasurementTraceIdentity(trace)}-${idx}`;

      if (interactive) {
        return (
          <button
            key={key}
            type="button"
            aria-label={`Preview trace ${formatMeasurementTraceIdentityLabel(trace)}`}
            aria-pressed={isDraftTrace}
            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-left transition ${
              isDraftTrace
                ? 'border-blue-500 ring-2 ring-blue-100'
                : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/40'
            } disabled:cursor-not-allowed disabled:opacity-60`}
            onClick={() => previewMeasurementTrace(trace)}
            disabled={preparationBlocked || savingMeasurementTrace}
          >
            {content}
          </button>
        );
      }

      return (
        <div
          key={key}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2.5"
        >
          {content}
        </div>
      );
    };

    return (
      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-slate-900">Measurements</h2>

        {importErrors.length > 0 && (
          <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
            <div className="flex items-center justify-between">
              <p className="font-semibold">Import errors</p>
              <button
                className="text-xs text-rose-600 hover:underline"
                onClick={() => setImportErrors([])}
              >
                Dismiss
              </button>
            </div>
            <ul className="mt-1 list-disc pl-5 space-y-1">
              {importErrors.map((err, i) => (
                <li key={i}>{err.filename || err.file || 'File'}: {err.error || err.detail || 'Unknown error'}</li>
              ))}
            </ul>
          </div>
        )}

        {compact ? <div aria-label="Uploaded files" className="rounded border border-slate-200 p-3">
          <p className="mb-2 text-sm text-slate-600">Files in the current pipeline cycle. Open Configure to review and save trace selections.</p>
          {!measurements.length && <p>No measurements yet. Upload instrument files.</p>}
          <ul className="divide-y divide-slate-200">{measurements.map((m) => <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div><span className="font-medium">{m.filename || 'Measurement'}</span> · <span>{statusLabel(m)}</span>
              {m.reason_message && <p className="text-sm text-rose-700">{m.reason_code || 'Import issue'}: {m.reason_message}</p>}
            </div>
            {renderMeasurementAction(m)}
          </li>)}</ul>
          {measurements.length > 0 && <button className="mt-3 rounded border px-3 py-2 font-semibold text-blue-700 hover:bg-blue-50" onClick={() => setActiveTab('configure')}>Open Configure</button>}
        </div> : (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Filename</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Pipeline</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Role</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Trace</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Sample Name</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Linked Sample</th>
                <th className="px-3 py-2 text-right font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {measurements.map((m) => (
                <React.Fragment key={m.id}>
                  <tr className={m.is_excluded ? 'bg-slate-50 opacity-60' : ''}>
                    <td className="px-3 py-2 font-medium text-slate-900">{m.filename || '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {m.pipeline === 'encapsulation'
                        ? 'Encapsulation'
                        : m.pipeline === 'size'
                          ? 'Size'
                          : '\u2014'}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{m.file_role || '\u2014'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        m.derived_status === 'ready' ? 'bg-emerald-100 text-emerald-800' :
                        m.derived_status === 'broken' ? 'bg-rose-100 text-rose-800' :
                        m.derived_status === 'excluded' ? 'bg-slate-100 text-slate-600' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {statusLabel(m)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{traceCell(m)}</td>
                    <td className="px-3 py-2 text-slate-600">{m.sample_name || '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-600">{resolveLinkedSampleName(m)}</td>
                    <td className="px-3 py-2 text-right">
                      {renderMeasurementAction(m)}
                    </td>
                  </tr>
                  {m.derived_status === 'broken' && (
                    <tr>
                      <td colSpan={8} className="bg-rose-50 px-3 py-2 text-xs text-rose-700">
                        <span className="font-semibold">{m.reason_code || 'error'}</span>: {m.reason_message || 'Unknown error'}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!measurements.length && (
                <tr>
                  <td className="px-3 py-4 text-center text-slate-500" colSpan={8}>
                    No measurements yet. Upload instrument files.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {previewMeasurement && (
          <div className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden bg-black/50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="measurement-preview-title"
              className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
            >
              <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
                <div>
                  <h4
                    id="measurement-preview-title"
                    className="text-lg font-semibold text-slate-900"
                  >
                    Preview: {previewMeasurement.filename || 'Measurement'}
                  </h4>
                  <p className="mt-0.5 text-sm text-slate-600">
                    {previewMeasurement.file_role ? `${previewMeasurement.file_role} \u2022 ` : ''}
                    {measurementPreview
                      ? `Showing ${measurementPreview.points.length} of ${measurementPreview.total_points} points${measurementPreview.sampled ? " (sampled across the full time range)" : ""}`
                      : measurementPreviewError ? 'No preview available' : 'Loading\u2026'}
                  </p>
                  {displayedTraceIdentity && (
                    <p className="text-sm text-slate-600">
                      Displayed trace: {formatMeasurementTraceIdentityLabel(displayedTraceIdentity)}
                    </p>
                  )}
                  {measurementPreviewError && (
                    <p className="text-sm text-rose-600 mt-1">{measurementPreviewError}</p>
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Close trace preview"
                  className="rounded p-1 text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  onClick={closeMeasurementPreviewModal}
                  disabled={savingMeasurementTrace}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div
                data-testid="trace-modal-scroll-region"
                className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-4"
              >
                {measurementTraces && traceOptions.length > 0 && (
                  <div className="space-y-4 text-sm">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h5 className="font-semibold text-slate-800">
                            {preparationBlocked
                              ? 'Selected analysis trace'
                              : 'Choose an analysis trace'}
                          </h5>
                          <p className="mt-1 text-xs text-slate-500">
                            {preparationBlocked
                              ? 'Saved trace selection is read-only.'
                              : 'Click a trace card to preview it immediately. Confirm only when you want to save the choice.'}
                          </p>
                        </div>
                        {recommendedTrace && !preparationBlocked && (
                          <button
                            type="button"
                            className="shrink-0 rounded-md border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
                            onClick={() => previewMeasurementTrace(recommendedTrace)}
                            disabled={savingMeasurementTrace}
                          >
                            Use recommended
                          </button>
                        )}
                      </div>
                      {!recommendedTrace && (
                        <p className="mt-2 text-xs text-amber-700" role="status">
                          {measurementTraces.ambiguity_reason === 'no_eligible_trace'
                            ? 'No eligible trace is available.'
                            : measurementTraces.ambiguity_reason ===
                                'top_scores_within_tolerance'
                              ? 'Top eligible trace scores are ambiguous.'
                              : 'No recommendation is available.'}
                        </p>
                      )}
                    </div>

                    {eligibleTraceOptions.length > 0 && (
                      <div>
                        <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Available for analysis
                        </h5>
                        <div className="space-y-2">
                          {eligibleTraceOptions.map((trace, idx) =>
                            renderTraceAssessment(trace, idx, !preparationBlocked)
                          )}
                        </div>
                      </div>
                    )}

                    {unavailableTraceOptions.length > 0 && (
                      <details className="rounded-lg border border-slate-200 bg-slate-50">
                        <summary className="cursor-pointer px-3 py-2.5 text-sm font-medium text-slate-700">
                          Unavailable instrument channels ({unavailableTraceOptions.length})
                        </summary>
                        <div className="space-y-2 border-t border-slate-200 p-3">
                          {unavailableTraceOptions.map((trace, idx) =>
                            renderTraceAssessment(trace, idx, false)
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {loadingMeasurementTraces && !measurementTraces && (
                  <p className="text-sm text-slate-600">Loading traces\u2026</p>
                )}

                {loadingMeasurementPreview && (
                  <p className="text-sm text-slate-600">Loading preview\u2026</p>
                )}

                {measurementPreview && !loadingMeasurementPreview && (
                  <div className="rounded-lg border border-slate-200 p-3">
                    <SizePlot
                      data={measurementPreview.points}
                      lines={[{ dataKey: 'signal', name: 'Signal', color: '#2563eb' }]}
                      title="Time vs Signal"
                    />
                  </div>
                )}
              </div>

              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
                <p className="text-xs text-slate-500">
                  {preparationBlocked
                    ? 'Trace selection is locked because preparation is protected.'
                    : 'Changes are saved only when you confirm.'}
                </p>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    onClick={closeMeasurementPreviewModal}
                    disabled={savingMeasurementTrace}
                  >
                    Cancel
                  </button>
                  {!preparationBlocked && previewMeasurement?.pipeline === 'size' && (
                    <button type="button" className="rounded border px-4 py-2"
                      disabled={savingMeasurementTrace || loadingMeasurementTraces || (!measurementTraces?.selected_trace_id && measurementTraces?.selected_wavelength_nm == null)}
                      onClick={() => confirmMeasurementTraceChange(true)}>Reset saved trace</button>
                  )}
                  {!preparationBlocked && previewMeasurement?.pipeline === 'size' && (
                    <button
                      type="button"
                      className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                      onClick={() => confirmMeasurementTraceChange()}
                      disabled={
                        savingMeasurementTrace ||
                        loadingMeasurementTraces ||
                        loadingMeasurementPreview ||
                        !traceSelectionChanged
                      }
                    >
                      {savingMeasurementTrace ? 'Saving\u2026' : 'Confirm selection'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderGroupingSection = () => {
    if (!measurements.some((m) => m.status === 'accepted' && !m.is_excluded)) {
      return <p role="status" className="rounded border bg-slate-50 p-3 text-sm text-slate-600">
        No accepted, included Measurements are available for grouping. Review rejected or excluded files in Upload.
      </p>;
    }
    const renderPreviewTag = (label, value, tone = 'slate') => {
      if (!value) return null;
      const palette = {
        slate: 'border-slate-200 bg-slate-100 text-slate-700',
        amber: 'border-amber-200 bg-amber-100 text-amber-800',
        emerald: 'border-emerald-200 bg-emerald-100 text-emerald-800',
        sky: 'border-sky-200 bg-sky-100 text-sky-800',
      };
      return (
        <span
          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${palette[tone] || palette.slate}`}
        >
          {label}: {value}
        </span>
      );
    };
    const statusBadgeLabel = (measurement) => {
      switch (measurement?.derived_status) {
        case 'ready':
          return 'READY';
        case 'ungrouped':
          return 'UNGROUPED';
        case 'needs_label':
          return 'NEEDS LABEL';
        case 'excluded':
          return 'EXCLUDED';
        case 'broken':
          return 'BROKEN';
        default:
          return '—';
      }
    };
    const measurementDisplayName = (measurementId) => {
      const measurement = measurementsById.get(measurementId);
      if (!measurement) return `Measurement #${measurementId}`;
      return measurement.filename || `Measurement #${measurementId}`;
    };
    const toggleClusterExpanded = (clusterIdx) => {
      setExpandedGroupingClusters((prev) => ({
        ...prev,
        [clusterIdx]: !prev[clusterIdx],
      }));
    };
    const handleMove = (measurementId, value) => {
      if (isFinalized || isVoid) return;
      if (value === 'unclusterable') {
        moveMeasurementTo(measurementId, 'unclusterable');
        return;
      }
      if (value.startsWith('cluster:')) {
        const idx = Number(value.split(':')[1]);
        if (Number.isInteger(idx)) moveMeasurementTo(measurementId, 'cluster', idx);
      }
    };
    const handleAutoGroup = async () => {
      if (preparationBlocked || groupingAutoLoading || groupingConfirming) return;
      setRecalculateOpen(false);
      setGroupingAutoLoading(true);
      try {
        const result = await autoGroupBatch(batchId, selectedPipeline);
        if (result.pipeline !== selectedPipeline || result.cycle_number !== preparationState.current_cycle_number ||
            !Number.isInteger(result.preparation_revision) || result.preparation_revision < 0) {
          setGroupingDraft(null);
          setPreparationNotice('Proposal scope changed or is missing. Refresh and generate a new proposal.');
          await refreshPreparation();
          return;
        }
        const draft = normalizeGroupingDraft(result);
        setPreparationNotice(null);
        setGroupingDraft(draft);
        await refreshPreparation();
        setExpandedGroupingClusters(
          Object.fromEntries(draft.clusters.map((_, idx) => [idx, true]))
        );
      } catch (err) {
        if (!(await handlePreparationConflict(err))) setPreparationNotice(extractApiError(err, 'Auto-grouping failed.'));
      } finally {
        setGroupingAutoLoading(false);
      }
    };

    const handleConfirmGrouping = async () => {
      if (preparationBlocked || groupingConfirming || !groupingValidation.canConfirm ||
          groupingDraft.pipeline !== selectedPipeline || groupingDraft.cycle_number !== preparationState.current_cycle_number) return;
      setGroupingConfirming(true);
      try {
        const payload = groupingDraft.clusters.map((cluster) => ({
          sample_name: cluster.sample_name.trim(),
          measurement_ids: cluster.measurement_ids,
        }));
        const result = await confirmGrouping(
          batchId,
          payload,
          groupingDraft.unclusterable,
          { pipeline: groupingDraft.pipeline, cycle_number: groupingDraft.cycle_number,
            preparation_revision: groupingDraft.preparation_revision }
        );
        setGroupingDraft(null);
        setExpandedGroupingClusters({});
        await refreshAfterPreparationMutation();
        if (result?.errors?.length) {
          window.alert(
            `Grouping completed with errors:\n${result.errors.map((e) => `${e.sample_name}: ${e.error}`).join('\n')}`
          );
        }
      } catch (err) {
        if (!(await handlePreparationConflict(err))) setPreparationNotice(extractApiError(err, 'Confirm grouping failed.'));
      } finally {
        setGroupingConfirming(false);
      }
    };

    return (
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-900">Grouping</h2>

        {noProject && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Batch must have a project assigned before grouping is available.
          </div>
        )}
        {isFinalized && (
          <p className="text-sm text-slate-500">
            Grouping is locked after finalization. Historical preparation remains read-only.
          </p>
        )}

        <button
          className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          onClick={() => {
            if (groupingDraft || hasPersistedGroupingDecisions) {
              setRecalculateOpen(true);
            } else {
              handleAutoGroup();
            }
          }}
          disabled={
            isFinalized ||
            noProject ||
            groupingAutoLoading ||
            groupingConfirming ||
            isVoid
          }
        >
          {groupingAutoLoading ? 'Analyzing…' : 'Auto-suggest grouping'}
        </button>

        {recalculateOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Recalculate grouping?"
              className="max-w-lg space-y-4 rounded-xl bg-white p-6 shadow-2xl"
            >
              <p className="text-sm text-slate-700">
                Auto-suggest will recalculate grouping for every measurement and may replace your manual assignments or Unclusterable decisions. Continue?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                  onClick={() => setRecalculateOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                  onClick={handleAutoGroup}
                >
                  Recalculate grouping
                </button>
              </div>
            </div>
          </div>
        )}

        {groupingDraft && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-800">
              Clusters ({groupingDraft.clusters?.length || 0})
            </h3>

            <div className="flex flex-wrap gap-2">
              <button
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => {
                  addGroupingCluster();
                  setExpandedGroupingClusters((prev) => ({
                    ...prev,
                    [groupingDraft.clusters.length]: true,
                  }));
                }}
                disabled={isFinalized || isVoid}
              >
                Add cluster
              </button>
              <button
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                onClick={() =>
                  setExpandedGroupingClusters(
                    Object.fromEntries(groupingDraft.clusters.map((_, idx) => [idx, true]))
                  )
                }
                disabled={!groupingDraft.clusters.length}
              >
                Expand all
              </button>
              <button
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                onClick={() =>
                  setExpandedGroupingClusters(
                    Object.fromEntries(groupingDraft.clusters.map((_, idx) => [idx, false]))
                  )
                }
                disabled={!groupingDraft.clusters.length}
              >
                Collapse all
              </button>
            </div>

            {groupingDraft.clusters?.map((cluster, i) => {
              const clusterPreview = buildClusterPreview(
                cluster,
                groupingDraft.measurementPreviewById
              );
              return (
              <div
                key={i}
                className="rounded border border-slate-200 bg-white p-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <input
                      className="w-full max-w-md rounded border border-slate-300 px-2 py-1 text-sm"
                      value={cluster.sample_name}
                      onChange={(e) => renameGroupingCluster(i, e.target.value)}
                      disabled={isFinalized || isVoid}
                      placeholder={`Cluster ${i + 1} sample name`}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      {cluster.measurement_ids.length} measurement{cluster.measurement_ids.length === 1 ? '' : 's'}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {renderPreviewTag('Confidence', clusterPreview.confidence, 'sky')}
                      {clusterPreview.confirmation_effect === 'reuse_existing_sample' &&
                        renderPreviewTag('Confirmation', 'Reuse existing sample', 'emerald')}
                      {clusterPreview.confirmation_effect === 'create_new_sample' &&
                        renderPreviewTag('Confirmation', 'Create new sample', 'amber')}
                    </div>
                    {(clusterPreview.detected_conditions.length > 0 ||
                      clusterPreview.detected_timepoints.length > 0 ||
                      clusterPreview.detected_temperatures.length > 0) && (
                      <div className="mt-2 space-y-1 text-xs text-slate-600">
                        {clusterPreview.detected_conditions.length > 0 && (
                          <p>
                            Detected conditions: {clusterPreview.detected_conditions.join(', ')}
                          </p>
                        )}
                        {clusterPreview.detected_timepoints.length > 0 && (
                          <p>
                            Detected timepoints: {clusterPreview.detected_timepoints.join(', ')}
                          </p>
                        )}
                        {clusterPreview.detected_temperatures.length > 0 && (
                          <p>
                            Detected temperatures: {clusterPreview.detected_temperatures.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    {clusterPreview.existing_sample_match && (
                      <p className="mt-2 text-xs text-slate-600">
                        Existing sample match: {clusterPreview.existing_sample_match.name} (#{clusterPreview.existing_sample_match.id})
                      </p>
                    )}
                    {clusterPreview.warnings.length > 0 && (
                      <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                        <p className="font-semibold">Preview warnings</p>
                        {clusterPreview.warnings.map((warning, warningIdx) => (
                          <p key={warningIdx}>{warning}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-xs font-semibold text-slate-700 hover:underline"
                      onClick={() => toggleClusterExpanded(i)}
                    >
                      {expandedGroupingClusters[i] ? 'Hide files' : 'Show files'}
                    </button>
                    <button
                      className="text-xs font-semibold text-rose-700 hover:underline disabled:opacity-50"
                      onClick={() => {
                        removeGroupingCluster(i);
                        setExpandedGroupingClusters((prev) => {
                          const next = {};
                          Object.entries(prev).forEach(([key, value]) => {
                            const idx = Number(key);
                            if (idx < i) next[idx] = value;
                            if (idx > i) next[idx - 1] = value;
                          });
                          return next;
                        });
                      }}
                      disabled={isFinalized || isVoid}
                    >
                      Remove cluster
                    </button>
                  </div>
                </div>

                {expandedGroupingClusters[i] && (
                  <ul className="mt-2 space-y-2">
                    {cluster.measurement_ids.map((measurementId) => {
                      const measurement = measurementsById.get(measurementId);
                      const previewMeasurement =
                        groupingDraft.measurementPreviewById?.[measurementId] || null;
                      const parsed = previewMeasurement?.parsed || null;
                      return (
                        <li
                          key={measurementId}
                          className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-slate-900">
                              {previewMeasurement?.original_name || measurementDisplayName(measurementId)}
                            </p>
                            <p className="text-xs text-slate-500">
                              #{measurementId} • {statusBadgeLabel(measurement)}
                            </p>
                            {parsed && (
                              <>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {renderPreviewTag('Condition', parsed.condition)}
                                  {renderPreviewTag('Timepoint', parsed.timepoint)}
                                  {renderPreviewTag('Temperature', parsed.temperature)}
                                  {renderPreviewTag('Replicate', parsed.replicate)}
                                  {renderPreviewTag('Confidence', parsed.confidence, 'sky')}
                                </div>
                                {parsed.warnings?.length > 0 && (
                                  <div className="mt-1 text-xs text-amber-800">
                                    {parsed.warnings.map((warning, warningIdx) => (
                                      <p key={warningIdx}>{warning}</p>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <select
                            className="rounded border border-slate-300 px-2 py-1 text-xs"
                            value={`cluster:${i}`}
                            disabled={isFinalized || isVoid}
                            onChange={(e) => handleMove(measurementId, e.target.value)}
                          >
                            {groupingDraft.clusters.map((targetCluster, idx) => (
                              <option key={idx} value={`cluster:${idx}`}>
                                {targetCluster.sample_name.trim() || `Cluster ${idx + 1}`}
                              </option>
                            ))}
                            <option value="unclusterable">Unclusterable</option>
                          </select>
                        </li>
                      );
                    })}
                    {!cluster.measurement_ids.length && (
                      <li className="rounded border border-dashed border-slate-300 px-2 py-2 text-xs text-slate-500">
                        No measurements in this cluster.
                      </li>
                    )}
                  </ul>
                )}
              </div>
              );
            })}

            {groupingDraft.unclusterable?.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="font-semibold text-amber-800">
                  Unclusterable ({groupingDraft.unclusterable.length})
                </p>
                <ul className="mt-2 space-y-2">
                  {groupingDraft.unclusterable.map((measurementId) => {
                    const measurement = measurementsById.get(measurementId);
                    return (
                      <li
                        key={measurementId}
                        className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-white px-2 py-1.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-slate-900">
                            {measurementDisplayName(measurementId)}
                          </p>
                          <p className="text-xs text-slate-500">
                            #{measurementId} • {statusBadgeLabel(measurement)}
                          </p>
                        </div>
                        <select
                          className="rounded border border-slate-300 px-2 py-1 text-xs"
                          value="unclusterable"
                          disabled={isFinalized || isVoid}
                          onChange={(e) => handleMove(measurementId, e.target.value)}
                        >
                          <option value="unclusterable">Unclusterable</option>
                          {groupingDraft.clusters.map((targetCluster, idx) => (
                            <option key={idx} value={`cluster:${idx}`}>
                              {targetCluster.sample_name.trim() || `Cluster ${idx + 1}`}
                            </option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {(groupingValidation.emptyNameClusters.length > 0 || groupingValidation.emptyClusters.length > 0 || groupingValidation.duplicateIds.length > 0) && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
                <p className="font-semibold">Fix these before confirming:</p>
                {groupingValidation.emptyNameClusters.length > 0 && (
                  <p>
                    Missing sample names: {groupingValidation.emptyNameClusters.map((idx) => idx + 1).join(', ')}
                  </p>
                )}
                {groupingValidation.emptyClusters.length > 0 && (
                  <p>
                    Empty clusters: {groupingValidation.emptyClusters.map((idx) => idx + 1).join(', ')}
                  </p>
                )}
                {groupingValidation.duplicateIds.length > 0 && (
                  <p>
                    Duplicate measurements across clusters: {Array.from(new Set(groupingValidation.duplicateIds)).join(', ')}
                  </p>
                )}
              </div>
            )}

            <button
              className="rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
              onClick={handleConfirmGrouping}
              disabled={
                groupingConfirming ||
                isFinalized ||
                isVoid ||
                noProject ||
                !groupingValidation.canConfirm
              }
            >
              {groupingConfirming ? 'Confirming…' : 'Confirm grouping'}
            </button>
          </div>
        )}
      </section>
    );
  };

  const tabs = [
    { key: 'data', label: 'Upload' },
    { key: 'grouping', label: 'Assign Samples' },
    { key: 'configure', label: 'Configure' },
    { key: 'review', label: 'Review' },
    { key: 'run', label: 'Run' },
    { key: 'runs', label: 'Results' },
  ];
  const secondaryTabs = [
    { key: 'history', label: `Measurement history (${historyMeasurements.length})` },
    { key: 'size-clusters', label: 'Size clusters' },
    { key: 'audit', label: 'Audit' },
  ];

  return (
    <div className="space-y-6 p-3 sm:p-4 lg:p-6">
      <BatchHeader
        batch={batch}
        samples={samples}
        showMetaEditor={showMetaEditor}
        setShowMetaEditor={setShowMetaEditor}
        metadataFields={metadataFields}
        setMetadataFields={setMetadataFields}
        onMetaSave={handleMetaSave}
        savingMeta={savingMeta}
        canEditMeta={isDraft && !isVoid}
      />

      <div role="tablist" aria-label="Pipeline preparation" className="flex gap-3">
        {PREPARATION_PIPELINES.map((pipeline) => (
          <button key={pipeline} role="tab" aria-selected={selectedPipeline === pipeline}
            onClick={() => { setSelectedPipeline(pipeline); setRecalculateOpen(false); }}
            className={`rounded border px-4 py-2 font-semibold ${selectedPipeline === pipeline ? 'bg-blue-700 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'}`}>
            {pipeline === 'size' ? 'Size preparation' : 'Encapsulation preparation'}
          </button>
        ))}
      </div>
      <div aria-label="Preparation scope" className="rounded border p-3">
        {preparationState ? <p>Current cycle {preparationState.current_cycle_number} · Preparation revision {preparationState.preparation_revision}</p>
          : <p role="status">Preparation state unavailable. Reload the workspace before preparing Measurements.</p>}
        {preparationState?.blocking_reason && <p role="status">Preparation protected: {preparationState.blocking_reason.detail} ({preparationState.blocking_reason.code})</p>}
        {preparationNotice && <p role="status">{preparationNotice}</p>}
        <p className="text-sm">Preparation availability does not establish scientific readiness or grant editing permission.</p>
      </div>

      <nav aria-label="Laboratory workflow" className="overflow-x-auto border-b border-slate-200">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`shrink-0 whitespace-nowrap rounded border px-4 py-3 text-sm font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              disabled={tab.disabled}
              aria-current={activeTab === tab.key ? 'step' : undefined}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <nav aria-label="Workspace history" className="flex flex-wrap gap-3">
        {secondaryTabs.map((tab) => <button key={tab.key} className="rounded border px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
          aria-current={activeTab === tab.key ? 'page' : undefined} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>)}
      </nav>

        <InitialRunPreparation key={batchId} view={activeTab} onNavigate={setActiveTab} batchId={batchId} pipeline={selectedPipeline}
          state={preparationState} measurements={measurements} blocked={preparationBlocked}
          createClientRequestId={generateClientRequestId} onRefresh={refreshAfterPreparationMutation}
          onConflict={handlePreparationConflict} runs={runs} onRefreshRuns={refreshInitialExecution}
          organizationId={batch?.project_detail?.organization} getSampleLabel={sampleLabelById} getInputLabel={measurementLabelById}
          onOpenRun={handleOpenActiveRunById} />
      {activeTab === 'data' && (
        <>
          {renderImportsSection()}
          {renderMeasurementsSection(true)}

        </>
      )}
      {activeTab === 'grouping' && (
        <>
          {measurements.length === 0 && <section aria-label="Empty assignment cycle" className="rounded border border-blue-200 bg-blue-50 p-4 space-y-2">
            <h2 className="font-semibold">No Measurements in this preparation cycle</h2>
            <p>Upload files for {selectedPipeline === 'size' ? 'Size' : 'Encapsulation'} cycle {preparationState?.current_cycle_number ?? '—'} before assigning Samples. Existing Samples below are a read-only overview; their identities persist across cycles.</p>
            <button className="rounded bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800" onClick={() => setActiveTab('data')}>Go to Upload</button>
          </section>}
          {renderSamplesSection()}
          {measurements.length > 0 && <>
          <section aria-label="Unassigned Measurements" className="rounded border p-4">
            <h2 className="font-semibold">Unassigned Measurements</h2>
            {measurements.filter((m) => m.sample_id == null).length === 0 ? <p>No unassigned Measurements in this cycle.</p> :
              <ul>{measurements.filter((m) => m.sample_id == null).map((m) => <li key={m.id}>{m.filename} · {m.status}{m.is_excluded ? ' · Excluded' : ''}</li>)}</ul>}
          </section>
          <fieldset disabled={preparationBlocked}>{renderGroupingSection()}</fieldset>
          </>}
        </>
      )}
      {activeTab === 'configure' && <section className="space-y-4">
        <h2 className="text-xl font-semibold">Configure {selectedPipeline === 'size' ? 'Size' : 'Encapsulation'}</h2>
        <p>Saved preparation is used for New Run. Previewing a trace does not save it.</p>
        {selectedPipeline === 'size' && renderMeasurementsSection()}
        <p className="text-sm text-slate-600">Initial analysis uses the existing analysis defaults. Parameter changes for Derived New Run remain available from Results.</p>
      </section>}
      {activeTab === 'review' && <section aria-label="Cycle review" className="rounded border p-4 space-y-2">
        <h2 className="text-xl font-semibold">Review current cycle</h2>
        <p>{measurements.length} Measurements · {measurements.filter((m) => m.status === 'accepted' && !m.is_excluded).length} accepted and included · {measurements.filter((m) => m.is_excluded).length} excluded · {measurements.filter((m) => m.status === 'rejected').length} rejected</p>
        <p>{new Set(measurements.map((m) => m.sample_id).filter((id) => id != null)).size} assigned Samples · {measurements.filter((m) => m.sample_id == null).length} unassigned Measurements</p>
        <ul>{measurements.map((m) => <li key={m.id}>{m.filename} · {m.sample_id == null ? 'Unassigned' : sampleLabelById(m.sample_id)} · {m.status}{m.is_excluded ? ' · Excluded' : ''}{selectedPipeline === 'size' ? ` · Saved trace: ${m.selected_trace_id || m.selected_wavelength_nm != null ? formatMeasurementTraceIdentityLabel({ trace_id: m.selected_trace_id, wavelength_nm: m.selected_wavelength_nm }) : 'Automatic recommendation (validated on admission)'}` : ` · Role: ${m.file_role || 'raw'}`}</li>)}</ul>
        <p>All accepted, included inputs must be grouped and scientifically valid. The backend validates the complete cycle on submission.</p>
      </section>}
      {activeTab === 'history' && <MeasurementHistory measurements={historyMeasurements} samples={samples} />}
      {activeTab === 'runs' && (
        <>
          {renderSamplesSection()}
          {renderRunsSection()}
        </>
      )}
      {activeTab === 'size-clusters' && renderSizeClustersSection()}
      {activeTab === 'audit' && renderAuditSection()}

      {editingSample && (
        <CompositionEditor
          sample={editingSample}
          onClose={() => setEditingSample(null)}
          onSave={async (composition) => {
            await handleCompositionChange(editingSample.id, composition);
            setEditingSample(null);
          }}
        />
      )}

      {eeErrors && (
        <EEErrorModal errors={eeErrors} onClose={() => setEeErrors(null)} />
      )}
      {pendingUploadPreview && (
        <UploadPreviewModal
          pendingUpload={pendingUploadPreview}
          uploading={sizeUploadState.uploading || eeUploadState.uploading}
          onClose={() => setPendingUploadPreview(null)}
          onRemoveFile={(index) => {
            setPendingUploadPreview((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                files: prev.files.filter((_, currentIndex) => currentIndex !== index),
              };
            });
          }}
          onConfirm={async () => {
            const upload = pendingUploadPreview;
            if (!upload?.files?.length) return;
            const ok = await executeImport(
              upload.files,
              upload.pipeline,
              upload.instrument,
              upload
            );
            if (ok) {
              setPendingUploadPreview(null);
            }
          }}
        />
      )}

    </div>
  );
}

function BatchHeader({
  batch,
  samples,
  showMetaEditor,
  setShowMetaEditor,
  metadataFields,
  setMetadataFields,
  onMetaSave,
  savingMeta,
  canEditMeta,
}) {
  const projectName =
    batch.project_detail?.name || batch.project_detail?.code || 'Unassigned';
  const preparedAt = batch.prepared_at
    ? new Date(batch.prepared_at).toLocaleString()
    : '—';
  const samplesCount =
    (samples && samples.length !== undefined ? samples.length : null) ??
    batch.sample_count ??
    '—';

  return (
    <div>
      <div className="border-b border-gray-200 px-6 pb-4 mb-4">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="break-words text-xl font-semibold text-gray-900">
              Batch {batch.code}
            </h2>
            <StatusBadge status={batch.status} />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm justify-end">
              {canEditMeta && (
                <button
                  className="rounded border border-slate-200 px-3 py-2 font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setShowMetaEditor((prev) => !prev)}
                >
                  {showMetaEditor ? 'Close' : 'Edit details'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 mb-2">
          <span className="text-gray-500 font-medium">Project:</span>
          <span className="text-gray-900">{projectName}</span>
        </div>

        <div className="text-sm mb-1">
          <div className="flex space-x-2">
            <span className="text-gray-500 font-medium">Prepared at:</span>
            <span className="text-gray-900">{preparedAt}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-y-1 text-sm">
          <div className="flex space-x-2">
            <span className="text-gray-500 font-medium">Samples:</span>
            <span className="text-gray-900">{samplesCount}</span>
          </div>
        </div>
      </div>

      {showMetaEditor && canEditMeta && (
        <form className="mt-2 border-t border-slate-100 pt-4" onSubmit={onMetaSave}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm">
              <span className="text-slate-600">Batch code</span>
              <input
                className="mt-1 w-full rounded border border-slate-300 p-2"
                value={metadataFields.code}
                disabled={!canEditMeta}
                onChange={(e) =>
                  setMetadataFields((prev) => ({ ...prev, code: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              disabled={savingMeta || !canEditMeta}
            >
              {savingMeta ? 'Saving…' : 'Save details'}
            </button>
          </div>
        </form>
      )}

    </div>
  );
}

function UploadPanel({
  icon,
  title,
  description,
  helper,
  children,
  uploading,
  summary,
  lastUploadedAt,
  error,
  onFiles,
  disabled = false,
  disabledMessage,
}) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length && onFiles) {
      onFiles(files);
    }
  };

  const handleSelect = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (disabled) return;
    if (files.length && onFiles) {
      onFiles(files);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="text-2xl">{icon}</div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
      </div>
      {children}
      <div
        className={`mt-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-sm ${
          dragActive ? 'border-slate-600 bg-slate-50' : 'border-slate-300'
        }`}
        aria-disabled={disabled}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(false);
        }}
        onDrop={handleDrop}
      >
        <p className="text-center text-slate-700">
          Drop files here or{' '}
          <button
            className="font-semibold text-slate-900 underline"
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            browse
          </button>
        </p>
        <p className="mt-2 text-xs text-slate-500">{helper}</p>
        <input
          type="file"
          multiple
          hidden
          aria-label={`${title} upload files`}
          ref={inputRef}
          onChange={handleSelect}
          disabled={disabled}
        />
        {uploading && <p className="mt-3 text-xs text-slate-500">Uploading…</p>}
      </div>
      <div className="mt-3 text-sm text-slate-600">
        <p>{summary}</p>
        {lastUploadedAt && (
          <p className="text-xs text-slate-400">
            Last upload: {lastUploadedAt.toLocaleString()}
          </p>
        )}
        {disabled && (
          <p className="text-xs text-slate-500">
            {disabledMessage || 'Uploads unavailable.'}
          </p>
        )}
        {error && <p className="text-xs text-rose-600">⚠ {error}</p>}
      </div>
    </div>
  );
}

function SamplesPanel({
  samples,
  totalSamples,
  groupedMeasurements,
  sampleFilters,
  setSampleFilters,
  showCompositionActions = false,
  canEditComposition = false,
  onConfirmComposition,
}) {
  const groupedMeasurementsBySampleId = new Map(
    (groupedMeasurements || []).map((group) => [String(group.sample_id), group.items || []])
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Samples ({samples.length} of {totalSamples})
          </h3>
          <p className="text-sm text-slate-500">
            Sample identity persists across pipeline cycles. Measurement assignments shown here belong only to the active pipeline cycle.
          </p>
        </div>
      </div>

      <p className="mt-2 text-sm text-slate-500">Composition is optional metadata. When provided, it belongs to Sample identity and can be confirmed only once.</p>
      <div className="mt-4 grid gap-3 text-sm md:grid-cols-4">
        <input
          className="rounded border border-slate-300 p-2"
          placeholder="Search name…"
          value={sampleFilters.search}
          onChange={(e) =>
            setSampleFilters((prev) => ({ ...prev, search: e.target.value }))
          }
        />

      </div>

      <div className="mt-4 overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">
                Sample
              </th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">
                Composition
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {samples.map((sample) => {
              const groupedItems =
                groupedMeasurementsBySampleId.get(String(sample.id)) || [];
              return (
                <tr key={sample.id}>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <span>{sample.name}</span>
                      {sample.status && (
                        <StatusBadge status={sample.status} />
                      )}
                    </div>
                    {groupedItems.length === 0 && <p className="mt-1 text-xs font-normal text-slate-500">Shared Sample · no Measurements in the active cycle</p>}
                    {groupedItems.length > 0 && (
                      <div className="mt-1 font-normal">
                        <p className="text-xs text-slate-500">
                          {groupedItems.length} current-cycle measurement{groupedItems.length === 1 ? '' : 's'}
                        </p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-slate-600">
                          {groupedItems.map((item) => (
                            <li key={item.id}>
                              {item.filename || `Measurement #${item.id}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <CompositionSummary composition={sample.composition} />
                    {sample.composition_confirmed ? <p className="mt-1 text-xs font-semibold text-emerald-700">Confirmed · read-only</p> : <>
                      <p className="mt-1 text-xs text-amber-700">Not yet confirmed</p>
                      {showCompositionActions && <button type="button" className="mt-2 rounded border px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        onClick={() => onConfirmComposition(sample)} disabled={!canEditComposition || sample.status === 'void'}>Enter composition</button>}
                    </>}
                  </td>
                </tr>
              );
            })}
        {!samples.length && (
          <tr>
            <td
              className="px-3 py-4 text-center text-slate-500"
              colSpan={2}
            >
              No samples yet. Upload files to populate this batch.
            </td>
          </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunsPanel({ runs, onSelectRun }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Saved runs for this batch</h3>
          <p className="text-sm text-slate-500">
            Open and inspect runs here in batch context. Original, exact rerun, and derived runs are shown explicitly.
          </p>
        </div>
      </div>
      <div className="mt-3 overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">
                Name
              </th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">
                Type
              </th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">
                Date
              </th>
              <th className="px-3 py-2 text-left font-semibold text-slate-600">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {runs.map((run) => (
              <tr
                key={run.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => onSelectRun(run)}
              >
                <td className="px-3 py-2 text-slate-900">
                  <div className="font-medium"><button className="text-left underline" onClick={(event) => { event.stopPropagation(); onSelectRun(run); }}>{run.name || 'Saved run'}</button></div>
                  <span>Pipeline: {run.pipeline || 'Unknown'} · </span><PreparationIdentity identity={run} />
                  <RunInputFiles inputFiles={run.input_files} />
                </td>
                <td className="px-3 py-2 text-slate-500">
                  {run.run_type || 'Saved run'}
                </td>
                <td className="px-3 py-2 text-slate-500">
                  {formatRunTimestamp(run.date)}
                </td>
                <td className="px-3 py-2 text-slate-500">
                  <StatusBadge status={run.status} />
                </td>
              </tr>
            ))}
            {!runs.length && (
              <tr>
                <td className="px-3 py-4 text-center text-slate-500" colSpan={4}>
                  No runs yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComponentTable({ title, components, emptyText, showReason = false }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
      <div className="mt-2 overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Measurement</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Peak</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">t0</th>
              <th className="px-2 py-2 text-left font-semibold text-slate-600">Dh</th>
              {showReason && (
                <th className="px-2 py-2 text-left font-semibold text-slate-600">Reason</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {components.map((component, idx) => (
              <tr key={`${component.workflow_run_id || 'run'}-${component.input_import_id || idx}-${component.peak_index || 0}-${component.component_index || 0}-${idx}`}>
                <td className="px-2 py-2 text-slate-600">
                  {component.input_import_id || '—'}
                </td>
                <td className="px-2 py-2 text-slate-600">
                  {component.peak_index ?? '—'}.{component.component_index ?? '—'}
                </td>
                <td className="px-2 py-2 text-slate-600">
                  {formatClusterNumber(component.t0, 2)}
                </td>
                <td className="px-2 py-2 text-slate-600">
                  {formatClusterNumber(component.dh_nm, 2)} nm
                </td>
                {showReason && (
                  <td className="px-2 py-2 text-slate-600">
                    {component.reason || '—'}
                  </td>
                )}
              </tr>
            ))}
            {!components.length && (
              <tr>
                <td
                  className="px-2 py-3 text-center text-slate-500"
                  colSpan={showReason ? 5 : 4}
                >
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityPanel({ events }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Activity</h3>
        <p className="text-sm text-slate-500">
          Latest 50 actions across batch, samples, and runs.
        </p>
      </div>
      <ul className="mt-4 space-y-3 text-sm">
        {events.map((event) => {
          const eventTime = event.created_at || event.timestamp;
          return (
            <li
              key={`${event.object_type}-${event.object_id}-${eventTime || 'unknown'}`}
              className="rounded border border-slate-100 bg-slate-50 px-3 py-2"
            >
              <p className="font-semibold text-slate-800 flex justify-between">
                <span>{formatAuditEvent(event)}</span>
                <span className="text-xs font-normal text-slate-500">
                  {eventTime ? new Date(eventTime).toLocaleString() : '—'}
                </span>
              </p>
            </li>
          );
        })}
        {!events.length && (
          <li className="rounded border border-dashed border-slate-200 px-3 py-4 text-center text-slate-500">
            No events yet.
          </li>
        )}
      </ul>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    running: 'bg-amber-100 text-amber-800 border border-amber-200',
    completed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    failed: 'bg-rose-100 text-rose-800 border border-rose-200',
    pending: 'bg-slate-100 text-slate-700 border border-slate-200',
    draft: 'bg-slate-100 text-slate-700 border border-slate-200',
    ready: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    archived: 'bg-orange-100 text-orange-700 border border-orange-200',
    void: 'bg-rose-100 text-rose-700 border border-rose-200',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
        colors[status] ||
        'bg-slate-100 text-slate-700 border border-slate-200'
      }`}
    >
      {status ? status.toUpperCase() : '—'}
    </span>
  );
}

function CompositionSummary({ composition }) {
  const values = composition || {};
  const rows = [
    ['Phospholipid', values.phospholipidType, values.phospholipid],
    ['Cholesterol', values.cholesterolType, values.cholesterol],
    ['Ionizable', values.ionizableType, values.ionizable],
    ['PEG', values.pegType, values.peg],
  ];
  return (
    <table className="w-full text-xs">
      <tbody>
        {rows.map(([label, type, pct]) => (
          <tr key={label}>
            <td className="pr-2 font-semibold text-slate-600">{label}</td>
            <td className="pr-2 text-slate-500">{type || '—'}</td>
            <td className="text-slate-500">{pct ?? 0}%</td>
          </tr>
        ))}
        <tr>
          <td className="pr-2 font-semibold text-slate-600">Mix ratio</td>
          <td className="text-slate-500" colSpan={2}>
            {values.mixRatio || '—'}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function CompositionEditor({ sample, onClose, onSave }) {
  const [composition, setComposition] = useState(sample?.composition || {});
  const canConfirm =
    composition && Object.values(composition).some((value) => Boolean(value));

  if (!sample) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">
          Confirm composition – {sample.name}
        </h3>
        <div className="mt-4">
          <CompositionForm
            key={sample.id}
            initialValues={sample.composition || {}}
            onChange={setComposition}
          />
        </div>
        <div className="mt-6 flex justify-end gap-2 text-sm">
          <button
            className="rounded border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded bg-slate-900 px-4 py-2 font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onSave(composition)}
            disabled={!canConfirm}
          >
            Confirm composition
          </button>
        </div>
      </div>
    </div>
  );
}


function EEErrorModal({ errors, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">
          Encapsulation import issues
        </h3>
        <p className="mt-1 text-sm text-slate-600">{errors.detail}</p>
        <div className="mt-4 space-y-3 text-sm">
          {!!errors.invalid_files?.length && (
            <div>
              <p className="font-semibold text-slate-700">Invalid filenames</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-slate-600">
                {errors.invalid_files.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          )}
          {!!errors.missing_pairs?.length && (
            <div>
              <p className="font-semibold text-slate-700">Missing pairs</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-slate-600">
                {errors.missing_pairs.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadPreviewModal({ pendingUpload, uploading = false, onClose, onRemoveFile, onConfirm }) {
  const pipelineLabel =
    pendingUpload?.pipeline === 'encapsulation'
      ? 'Encapsulation measurements'
      : 'Size measurements';
  const files = pendingUpload?.files || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">Confirm upload</h3>
        <p className="mt-1 text-sm text-slate-600">
          {pipelineLabel} • {files.length} file{files.length === 1 ? '' : 's'} selected
        </p>
        <div className="mt-4 max-h-80 overflow-y-auto rounded border border-slate-200">
          <ul className="divide-y divide-slate-200 text-sm">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <span className="truncate text-slate-700">{file.name}</span>
                <button
                  type="button"
                  className="shrink-0 text-xs font-semibold text-rose-700 hover:underline"
                  onClick={() => onRemoveFile(index)}
                  disabled={uploading}
                >
                  Remove
                </button>
              </li>
            ))}
            {!files.length && (
              <li className="px-4 py-6 text-center text-sm text-slate-500">
                No files selected.
              </li>
            )}
          </ul>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            onClick={onConfirm}
            disabled={!files.length || uploading}
          >
            {uploading ? 'Uploading…' : 'Upload files'}
          </button>
        </div>
      </div>
    </div>
  );
}
